import React, { useEffect, useState } from "react";
import {
  FormControlLabel,
  Checkbox,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Typography,
  Grid,
  Box,
  makeStyles,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  FormControl,
  Select,
  Radio,
  Tab,
} from "@material-ui/core";
import {
  dateFormated,
  presentFormatedData,
} from "../../functionalComponents/dataFormatFunctions";
import { appUrl } from "../../../../../../utils/config";
import { useDispatch } from "react-redux";
import FlagIcon from "@material-ui/icons/Flag";
import {
  DESTROY_BACKDROP,
  OPEN_BACKDROP,
} from "../../../../containers/actions/types";
import axios from "axios";
import CloseIcon from "@material-ui/icons/Close";
import { ChevronRight } from "@material-ui/icons";
import BackDropLoading from "../../models/BackDrop";
import jwtDecode from "jwt-decode";
import { set } from "lodash";

// BCM-234 F9: Storage Location ID column label + classification.
// Rows with storageLocationID = 0 / "0" / "" / null / undefined are
// Special-Stocks-with-Vendor (SAP S=O). Anything else is a real SLoc ID
// (e.g. "0002", "0006").
const isSpecialStockWithVendor = (slocId) =>
  slocId == null || slocId === "" || slocId === 0 || slocId === "0";

const formatStorageLocation = (slocId) =>
  isSpecialStockWithVendor(slocId)
    ? "Special Stocks with Vendor"
    : String(slocId);

// Formats a batch's Net Available Qty. to 3 decimals for populating
// Planned Allocated Qty. when a deplete instruction is checked.
const formatNetAvailableQty = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const numericValue = Number(value);
  return isNaN(numericValue) ? value : numericValue.toFixed(3);
};

// BCM-234 F9: the same batch can now appear under more than one storage
// location (Special Stocks with Vendor + a real SLoc), so a row must be
// identified by storage location + batch. Keying on batch alone made the
// radio/qty match every row with that batch (selecting/filling both).
const lotRowKey = (item) =>
  `${item?.storageLocationID ?? ""}|${item?.inventoryBatchNumber ?? ""}`;

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    border: "2px solid #ccc",
    borderRadius: "8px",
    overflow: "auto",
    maxWidth: "99%",
  },
  commentField: {
    "& .MuiInputBase-input": {
      lineHeight: "1.2rem",
    },
  },

  headerCell: {
    backgroundColor: "#f5f5f5",
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.3,
    // BCM-234 F9: allow multi-word header labels (e.g. "Add Deplete
    // Instruction", "BMS Material Batch Number") to wrap naturally at
    // spaces so the table fits inside the popup. Single words like
    // "Selection" stay intact (no wordBreak, so they aren't split).
    whiteSpace: "normal",
    verticalAlign: "middle",
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: "8px 10px",
    borderBottom: "1px solid #d0d0d0",
  },
  customSelect: {
    minWidth: 150,
  },
  customRadio: {
    color: "#ec6535 !important", // Selected radio button color
  },
  customCheckbox: {
    color: "#ec6535 !important", // Checked checkbox color
  },
  customSelectSty: {
    "& .MuiSelect-outlined.MuiSelect-outlined": {
      paddingTop: "10px",
      paddingBottom: "10px",
    },
  },
  textField:{
    "& .MuiInputBase-root": {
    lineHeight:"0.6rem",
    },
  },
  tablepadding:{
    "& .MuiTableCell-root":{
      padding:"2px"
    }, 
  },
  secondTablepadding:{
    // BCM-234 F9: replace the tight 2px padding with a uniform cell padding
    // + a soft horizontal separator. Result is cleaner, more readable rows
    // with proper gap between columns; horizontal padding kept moderate so
    // all seven columns (incl. the new Storage Location ID) fit without
    // clipping the right-most "Add Deplete Instruction" column.
    "& .MuiTableCell-root": {
      padding: "8px 10px",
      borderBottom: "1px solid #eaeaea",
      fontSize: 13,
      verticalAlign: "middle",
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-root": {
      borderBottom: "none",
    },
    "& .MuiTableRow-root:hover": {
      backgroundColor: "#fafafa",
    },
  },
  updatedHeader: {
    // "& .MuiTableCell-root":{
    //   padding:"0px"
    // },
    fontWeight: 700,
    fontSize: 13.6,
    },
  updatedSubHeader: {
    fontSize: 13.6,
     padding:"0px"
  },
  updatedTableContainer: {
    // BCM-234 F9: lighter border + comfortable visible area. The table is
    // sized to fit the dialog width, so horizontal scroll is suppressed —
    // only the vertical scrollbar shows when rows exceed maxHeight.
    border: "1px solid #d6d6d6",
    borderRadius: 6,
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    maxWidth: "100%",
    maxHeight: 380,
    "&::-webkit-scrollbar": { width: 8 },
    "&::-webkit-scrollbar-thumb": { background: "#cfcfcf", borderRadius: 4 },
    "&::-webkit-scrollbar-thumb:hover": { background: "#b3b3b3" },
  },
}));

const EditPlannedAllocatedQuantityDialog = ({
  lotData,
  rmPmCode,
  onClose,
  poData,
  schrockenSLID,
  vendorMosymphonyID,
  handleClose,
  lotAllocationData,
  lotAllocationRes
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [updatedInventory, setUpdatedInventory] = useState(
    lotData?.inventoryDetails && Array.isArray(lotData.inventoryDetails)
      ? [...lotData.inventoryDetails]
      : []
  );
  
  const [allowMultipleLotAllocation , setAllowMultipleLotAllocation] = useState(false);
  const [allowOverAllocation , setAllowOverAllocation] = useState(false);
  const [overAllocationLimit , setOverAllocationLimit] = useState(0);
  const [allowUnderAllocation , setAllowUnderAllocation] = useState(false);
  const [underAllocationLimit , setUnderAllocationLimit] = useState(0);
  // Allocation warning configuration (BCM-613) — informational warning band only;
  // never affects errors or the Save button.
  const [warningMsgReqForUnderAllocation, setWarningMsgReqForUnderAllocation] = useState(false);
  const [underWarningRangeFrom, setUnderWarningRangeFrom] = useState(null);
  const [underWarningRangeTo, setUnderWarningRangeTo] = useState(null);
  const [warningMsgReqForOverAllocation, setWarningMsgReqForOverAllocation] = useState(false);
  const [overWarningRangeFrom, setOverWarningRangeFrom] = useState(null);
  const [overWarningRangeTo, setOverWarningRangeTo] = useState(null);
  const [inputErrors, setInputErrors] = useState({});
  const [totalAllocationError, setTotalAllocationError] = useState("");
  // BCM-234 F9: "Show batches from other storage locations for Plant <Plant ID>" checkbox.
  // Default unchecked = show only Special-Stocks-with-Vendor rows (today's behavior).
  // Checked = also show rows in the plant's other storage locations (0002, 0006, ...).
  const [showOtherStorageLocations, setShowOtherStorageLocations] = useState(false);

  useEffect(() => {
    if (
      Array.isArray(lotAllocationData) &&
      lotAllocationData.length > 0 &&
      Array.isArray(lotAllocationData[0].lotNumberAllocationDetails) &&
      lotAllocationData[0].lotNumberAllocationDetails.length > 0
    ) {
      setAllowMultipleLotAllocation(
        lotAllocationData[0].lotNumberAllocationDetails[0].allowMultipleLotAllocation
      );
      setAllowOverAllocation(false); // Always start unchecked
      setOverAllocationLimit(
        lotAllocationData[0].lotNumberAllocationDetails[0].overAllocationLimit
      );
      setAllowUnderAllocation(false);
      setUnderAllocationLimit(
        lotAllocationData[0].lotNumberAllocationDetails[0].allowUnderAllocationLimit ||
        lotAllocationData[0].lotNumberAllocationDetails[0].underAllocationLimit ||
        0
      );
      // Allocation warning configuration (BCM-613) — read from the same source
      // as the existing limits (lotNumberAllocationDetails[0])
      const lotConfig = lotAllocationData[0].lotNumberAllocationDetails[0];
      setWarningMsgReqForUnderAllocation(lotConfig.warningMessageRequiredForUnderAllocation === true);
      setUnderWarningRangeFrom(lotConfig.underAllocationWarningRangeFrom ?? null);
      setUnderWarningRangeTo(lotConfig.underAllocationWarningRangeTo ?? null);
      setWarningMsgReqForOverAllocation(lotConfig.warningMessageRequiredForOverAllocation === true);
      setOverWarningRangeFrom(lotConfig.overAllocationWarningRangeFrom ?? null);
      setOverWarningRangeTo(lotConfig.overAllocationWarningRangeTo ?? null);
    }
  }, [lotAllocationData]);

  const [reasonCodes, setReasonCodes] = useState([]);
  const [rmpmChecked, setrmpmChecked] = useState(true);
  const [selectedReason, setSelectedReason] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);
  const [netAvailableQty, setNetAvailableQty] = useState(null);
  const [selectedLotDataState, setSelectedLotDataState] = useState(null);

  // Enable Save button conditionally
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  
  // State to track deplete instruction checkboxes for each row
  // Key: row index, Value: boolean indicating if deplete instruction should be added
  const [depleteInstructions, setDepleteInstructions] = useState({});

  const [disableReasonCode, setDisableReasonCode] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  // ── Allocation warning (BCM-613) ─────────────────────────────────────────
  // Purely informational, derived from the warning band configured on the
  // product. It never sets an error and never touches isSaveEnabled — within
  // the warning band the Save button stays enabled exactly as before.
  // Band: deviation from the Material BoM Qty between rangeFrom% and rangeTo%.
  // Returns { type: 'under' | 'over' | null, message } — `type` also drives the
  // underAllocationDoneWithinWarningLimit / overAllocationDoneWithinWarningLimit
  // flags sent on save (Corda records the PO-history warning event from them),
  // so the flag always matches exactly what the user saw on screen.
  const allocationWarning = React.useMemo(() => {
    const none = { type: null, message: "" };
    const required = parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0);
    if (!required || required <= 0) return none;
    const inventory = Array.isArray(updatedInventory) ? updatedInventory : [];
    const total = inventory.reduce(
      (acc, item) => acc + (parseFloat(item.plannedAllocatedQuantity) || 0),
      0
    );
    if (!total || total <= 0) return none;
    // Suppress the warning while any blocking error is displayed
    const hasRowErrors = Object.values(inputErrors || {}).some(Boolean);
    if (hasRowErrors || totalAllocationError) return none;

    const totalQty = parseFloat(total.toFixed(3));
    const requiredQty = parseFloat(required.toFixed(3));

    // Under-allocation warning band
    if (
      totalQty < requiredQty &&
      allowUnderAllocation &&
      warningMsgReqForUnderAllocation &&
      underWarningRangeFrom != null &&
      underWarningRangeTo != null
    ) {
      const warnUpper = parseFloat((required - (underWarningRangeFrom * required / 100)).toFixed(3));
      const warnLower = parseFloat((required - (underWarningRangeTo * required / 100)).toFixed(3));
      if (totalQty >= warnLower && totalQty <= warnUpper) {
        return {
          type: 'under',
          message: `Under-Allocation Warning: The total Planned Allocated Qty. (${total.toFixed(3)}) is within the warning message limit of the under-allocation (${underWarningRangeFrom}% - ${underWarningRangeTo}% of Material BoM Qty: ${required.toFixed(3)}). You can still save.`,
        };
      }
    }

    // Over-allocation warning band
    if (
      totalQty > requiredQty &&
      allowOverAllocation &&
      warningMsgReqForOverAllocation &&
      overWarningRangeFrom != null &&
      overWarningRangeTo != null
    ) {
      const warnLower = parseFloat((required + (overWarningRangeFrom * required / 100)).toFixed(3));
      const warnUpper = parseFloat((required + (overWarningRangeTo * required / 100)).toFixed(3));
      if (totalQty >= warnLower && totalQty <= warnUpper) {
        return {
          type: 'over',
          message: `Over-Allocation Warning: The total Planned Allocated Qty. (${total.toFixed(3)}) is within the warning message limit of the over-allocation (${overWarningRangeFrom}% - ${overWarningRangeTo}% of Material BoM Qty: ${required.toFixed(3)}). You can still save.`,
        };
      }
    }

    return none;
  }, [
    updatedInventory,
    lotData,
    inputErrors,
    totalAllocationError,
    allowUnderAllocation,
    allowOverAllocation,
    warningMsgReqForUnderAllocation,
    underWarningRangeFrom,
    underWarningRangeTo,
    warningMsgReqForOverAllocation,
    overWarningRangeFrom,
    overWarningRangeTo,
  ]);
  // Sync inventoryDetails when it changes
  useEffect(() => {
    if (lotData?.inventoryDetails && Array.isArray(lotData.inventoryDetails)) {
      setUpdatedInventory([...lotData.inventoryDetails]);
      // Initialize deplete instructions state - all unchecked by default
      const initialDepleteInstructions = {};
      lotData.inventoryDetails.forEach((item, index) => {
        initialDepleteInstructions[index] = false;
      });
      setDepleteInstructions(initialDepleteInstructions);
    }
  }, [lotData]);

  useEffect(() => {
    
    dispatch({
      type: DESTROY_BACKDROP,
    });
    
    
    return () => {
      dispatch({
        type: DESTROY_BACKDROP,
      });
    };
  }, [dispatch]);
  
  // The optional baseInventory param lets a caller chain a "clear the previously
  // selected row" update and a "populate this row" update into a single, consistent
  // snapshot instead of racing two setState calls that both read the same stale
  // `updatedInventory` closure.
  const handleInputChange = (index, value, baseInventory = updatedInventory) => {
    if (!Array.isArray(baseInventory)) return;

    let enteredValue = value.match(/^[0-9]*\.?[0-9]{0,3}$/)
      ? value
      : baseInventory[index]?.plannedAllocatedQuantity || "";

    const newInventory = [...baseInventory];
    newInventory[index] = {
      ...newInventory[index],
      plannedAllocatedQuantity: enteredValue,
    };
    setUpdatedInventory(newInventory);

    // If quantity is cleared or zero, uncheck the deplete instruction for this row
    if (!rmpmChecked && (!enteredValue || parseFloat(enteredValue) <= 0)) {
      setDepleteInstructions((prev) => ({
        ...prev,
        [index]: false,
      }));
    }

    const componentQuantity = parseFloat(
      lotData?.componentAllocationDetails?.componentQuantity || 0
    );
    const enteredNumber = parseFloat(enteredValue);
    let errorMsg = "";

    const requiredQuantity = parseFloat(
      lotData?.componentAllocationDetails?.componentQuantity || 0
    );

    // Calculate total entered quantity
    const totalEnteredQuantity = newInventory.reduce(
      (acc, item) => acc + (parseFloat(item.plannedAllocatedQuantity) || 0),
      0
    );

    let totalErrorMsg = "";
    const isExactMatch = totalEnteredQuantity.toFixed(3) === requiredQuantity.toFixed(3);

    
    if (!rmpmChecked) {
     
      if (enteredValue !== "" && enteredNumber > 0) {
        const netAvailableQty = parseFloat(newInventory[index]?.netAvailableQuantity || 0);
        if (enteredNumber > netAvailableQty) {
          errorMsg = `Planned Allocated Qty. exceeds Net Available Qty. for this material batch number.`;
        }
      }

     
      const allRowErrors = {};
      newInventory.forEach((item, idx) => {
        const qty = parseFloat(item.plannedAllocatedQuantity || 0);
        if (qty > 0) {
          const netQty = parseFloat(item.netAvailableQuantity || 0);
          if (qty > netQty) {
            allRowErrors[idx] = `Planned Allocated Qty. exceeds Net Available Qty. for this material batch number.`;
          }
        }
      });

     
      const allRowLevelPassed = Object.keys(allRowErrors).length === 0;

      
      const enteredValuesCount = newInventory.filter(
        (item) => parseFloat(item.plannedAllocatedQuantity || 0) > 0
      ).length;

      
      if (allRowLevelPassed) {
        
        if (allowOverAllocation && enteredValue !== "" && enteredNumber > 0) {
         
          const maxAllowed = requiredQuantity + (overAllocationLimit * requiredQuantity / 100);
          
          if (enteredValuesCount === 1) {
            // Single value: validate individual value against calculated limit (BOM + over-allocation percentage)
            const enteredQty = parseFloat(enteredNumber.toFixed(3));
            const reqQty = parseFloat(requiredQuantity.toFixed(3));
            const maxAllowedQty = parseFloat(maxAllowed.toFixed(3));
            
            if (enteredQty > maxAllowedQty) {
              errorMsg = `Over-Allocation Limit Exceeded: The entered Planned Allocated Qty. (${enteredNumber.toFixed(3)}) exceeds the maximum allowed over-allocation limit of ${maxAllowed.toFixed(3)} (Material BoM Qty: ${requiredQuantity.toFixed(3)} + Over-allocation: ${overAllocationLimit}%).`;
            } else if (enteredQty < reqQty) {
              errorMsg = `Allocation Below Material BoM Qty: The entered Planned Allocated Qty. (${enteredNumber.toFixed(3)}) is less than Material BoM Qty. (${requiredQuantity.toFixed(3)}).`;
            }
          } else if (enteredValuesCount > 1) {
           
            errorMsg = ""; // Clear individual row error when multiple values are entered
            const totalQty = parseFloat(totalEnteredQuantity.toFixed(3));
            const reqQty = parseFloat(requiredQuantity.toFixed(3));
            const maxAllowedQty = parseFloat(maxAllowed.toFixed(3));
            
            if (totalQty > maxAllowedQty) {
              // Sum exceeds maxAllowed limit (BOM + over-allocation percentage) - show error with calculated limit
              totalErrorMsg = `Over-Allocation Limit Exceeded: The total Planned Allocated Qty. (${totalEnteredQuantity.toFixed(3)}) exceeds the maximum allowed over-allocation limit of ${maxAllowed.toFixed(3)} (Material BoM Qty: ${requiredQuantity.toFixed(3)} + Over-allocation: ${overAllocationLimit}%).`;
            } else if (totalQty < reqQty) {
              // Sum is below requiredQuantity - show error
              totalErrorMsg = `Allocation Below Material BoM Qty: The total Planned Allocated Qty. (${totalEnteredQuantity.toFixed(3)}) is less than Material BoM Qty. (${requiredQuantity.toFixed(3)}).`;
            }
           
          }
        } else if (allowUnderAllocation && enteredValue !== "" && enteredNumber > 0) {
          // Under allocation validation for multiple batch
          const minAllowed = requiredQuantity - (underAllocationLimit * requiredQuantity / 100);
          
          if (enteredValuesCount === 1) {
            // Single value: validate individual value against calculated limit (BOM - under-allocation percentage)
            const enteredQty = parseFloat(enteredNumber.toFixed(3));
            const reqQty = parseFloat(requiredQuantity.toFixed(3));
            const minAllowedQty = parseFloat(minAllowed.toFixed(3));
            
            if (enteredQty < minAllowedQty) {
              errorMsg = `Under-Allocation Limit Exceeded: The entered Planned Allocated Qty. (${enteredNumber.toFixed(3)}) is below the minimum allowed under-allocation limit of ${minAllowed.toFixed(3)} (Material BoM Qty: ${requiredQuantity.toFixed(3)} - Under-allocation: ${underAllocationLimit}%).`;
            } else if (enteredQty > reqQty) {
              errorMsg = `Allocation Above Material BoM Qty: The entered Planned Allocated Qty. (${enteredNumber.toFixed(3)}) exceeds the Material BoM Qty. (${requiredQuantity.toFixed(3)}).`;
            }
          } else if (enteredValuesCount > 1) {
           
            errorMsg = ""; // Clear individual row error when multiple values are entered
            const totalQty = parseFloat(totalEnteredQuantity.toFixed(3));
            const reqQty = parseFloat(requiredQuantity.toFixed(3));
            const minAllowedQty = parseFloat(minAllowed.toFixed(3));
            
            if (totalQty < minAllowedQty) {
              // Sum is below minAllowed limit (BOM - under-allocation percentage) - show error with calculated limit
              totalErrorMsg = `Under-Allocation Limit Exceeded: The total Planned Allocated Qty. (${totalEnteredQuantity.toFixed(3)}) is below the minimum allowed under-allocation limit of ${minAllowed.toFixed(3)} (Material BoM Qty: ${requiredQuantity.toFixed(3)} - Under-allocation: ${underAllocationLimit}%).`;
            } else if (totalQty > reqQty) {
              // Sum exceeds requiredQuantity - show error
              totalErrorMsg = `Allocation Above Material BoM Qty: The total Planned Allocated Qty. (${totalEnteredQuantity.toFixed(3)}) exceeds the Material BoM Qty. (${requiredQuantity.toFixed(3)}).`;
            }
          
          }
        } else {
          // No over/under allocation: use original BOM validation
          if (enteredValuesCount === 1 && enteredValue !== "" && enteredNumber > 0) {
            // If only one value entered, check individual value vs BOM Qty
            if (enteredNumber > requiredQuantity) {
              errorMsg = `Planned Allocated Qty. (${enteredNumber.toFixed(3)}) exceeds Material BoM Qty. (${requiredQuantity.toFixed(3)}).`;
            }
          } else if (enteredValuesCount > 1) {
            // If multiple values entered, check sum vs BOM Qty
            const totalQty = parseFloat(totalEnteredQuantity.toFixed(3));
            const reqQty = parseFloat(requiredQuantity.toFixed(3));
            if (totalQty > reqQty) {
              totalErrorMsg = `Total allocation mismatch: Sum of Planned Allocated Qty. (${totalEnteredQuantity.toFixed(3)}) exceeds Material BoM Qty. (${requiredQuantity.toFixed(3)}).`;
            }
          }
        }
      }

     
      setInputErrors((prev) => {
        const newErrors = { ...prev };
        // Update all row-level errors
        Object.keys(allRowErrors).forEach((key) => {
          newErrors[key] = allRowErrors[key];
        });
        // Update current row's BOM/over/under allocation error (if any)
        if (errorMsg && !allRowErrors[index]) {
          newErrors[index] = errorMsg;
        } else if (!errorMsg && !allRowErrors[index]) {
          // Clear BOM/over/under allocation error for current row if it passes
          const currentError = newErrors[index];
          if (currentError && (
            currentError.includes("exceeds Material BoM Qty") ||
            currentError.includes("Over-Allocation Limit Exceeded") ||
            currentError.includes("Under-Allocation Limit Exceeded") ||
            currentError.includes("Allocation Below Material BoM Qty") ||
            currentError.includes("Allocation Above Material BoM Qty")
          )) {
            delete newErrors[index];
          }
        }
        // Clear row-level errors for rows that now pass
        Object.keys(newErrors).forEach((key) => {
          if (!allRowErrors[key] && newErrors[key]?.includes("exceeds Net Available Qty")) {
            delete newErrors[key];
          }
        });
       
        if (enteredValuesCount > 1 && (allowOverAllocation || allowUnderAllocation)) {
          Object.keys(newErrors).forEach((key) => {
            const errorText = newErrors[key];
            if (errorText && (
              errorText.includes("Over-Allocation Limit Exceeded") ||
              errorText.includes("Under-Allocation Limit Exceeded") ||
              errorText.includes("Allocation Below Material BoM Qty") ||
              errorText.includes("Allocation Above Material BoM Qty")
            )) {
              delete newErrors[key];
            }
          });
        }
        // If sum error exists, clear all individual BOM/over/under allocation errors
        if (totalErrorMsg) {
          Object.keys(newErrors).forEach((key) => {
            const errorText = newErrors[key];
            if (errorText && (
              (errorText.includes("exceeds Material BoM Qty") && !errorText.includes("Allocation Above Material BoM Qty") && !errorText.includes("Allocation Below Material BoM Qty")) ||
              errorText.includes("Over-Allocation Limit Exceeded") ||
              errorText.includes("Under-Allocation Limit Exceeded") ||
              errorText.includes("Allocation Below Material BoM Qty") ||
              errorText.includes("Allocation Above Material BoM Qty")
            )) {
              delete newErrors[key];
            }
          });
        }
        return newErrors;
      });
    } else {
      
      totalErrorMsg = ""; // Clear sum errors for single batch
      
      if (enteredValue !== "" && enteredNumber > 0) {
        const netAvailableQty = parseFloat(newInventory[index]?.netAvailableQuantity || 0);
        if (enteredNumber > netAvailableQty) {
          errorMsg = `Planned Allocated Qty. (${enteredNumber.toFixed(3)}) exceeds Net Available Qty. (${netAvailableQty.toFixed(3)}) for this material batch number.`;
        } else {
          
          if (allowOverAllocation) {
            // Over allocation validation only - validate against calculated limit (BOM + over-allocation percentage)
            const maxAllowed = componentQuantity + (overAllocationLimit * componentQuantity / 100);
            const enteredQty = parseFloat(enteredNumber.toFixed(3));
            const compQty = parseFloat(componentQuantity.toFixed(3));
            const maxAllowedQty = parseFloat(maxAllowed.toFixed(3));
            
            if (enteredQty > maxAllowedQty) {
              errorMsg = `Over-Allocation Limit Exceeded: The entered Planned Allocated Qty. (${enteredNumber.toFixed(3)}) exceeds the maximum allowed over-allocation limit of ${maxAllowed.toFixed(3)} (Material BoM Qty: ${componentQuantity.toFixed(3)} + Over-allocation: ${overAllocationLimit}%).`;
            } else if (enteredQty < compQty) {
              errorMsg = `Allocation Below Material BoM Qty: The entered Planned Allocated Qty. (${enteredNumber.toFixed(3)}) is less than Material BoM Qty. (${componentQuantity.toFixed(3)}).`;
            }
          } else if (allowUnderAllocation) {
            // Under allocation validation only - validate against calculated limit (BOM - under-allocation percentage)
            const minAllowed = componentQuantity - (underAllocationLimit * componentQuantity / 100);
            const enteredQty = parseFloat(enteredNumber.toFixed(3));
            const compQty = parseFloat(componentQuantity.toFixed(3));
            const minAllowedQty = parseFloat(minAllowed.toFixed(3));
            
            if (enteredQty < minAllowedQty) {
              errorMsg = `Under-Allocation Limit Exceeded: The entered Planned Allocated Qty. (${enteredNumber.toFixed(3)}) is below the minimum allowed under-allocation limit of ${minAllowed.toFixed(3)} (Material BoM Qty: ${componentQuantity.toFixed(3)} - Under-allocation: ${underAllocationLimit}%).`;
            } else if (enteredQty > compQty) {
              errorMsg = `Allocation Above Material BoM Qty: The entered Planned Allocated Qty. (${enteredNumber.toFixed(3)}) exceeds the Material BoM Qty. (${componentQuantity.toFixed(3)}).`;
            }
          }
        }
      }

      
      setInputErrors((prev) => {
        const newErrors = { ...prev, [index]: errorMsg };
        
        Object.keys(newErrors).forEach((key) => {
          const errorText = newErrors[key];
          // Clear multiple allocation errors
          if (errorText && (
            errorText.includes("exceeds Net Available Qty") && !errorText.includes("exceeds Net Available Qty. (") || // Multiple allocation row-level error (no values)
            errorText.includes("exceeds Material BoM Qty") && !errorText.includes("Allocation Above Material BoM Qty") // Multiple allocation BOM error
          )) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
    
    setTotalAllocationError(totalErrorMsg);

    // Disable other inputs if one textbox equals required quantity
    const shouldDisableOthers = newInventory.some(
      (item) =>
        parseFloat(item.plannedAllocatedQuantity).toFixed(3) ===
        requiredQuantity.toFixed(3)
    );

    setUpdatedInventory(
      newInventory.map((item, i) => ({
        ...item,
        disabled: shouldDisableOthers && i !== index,
      }))
    );

    
    let hasRowErrors = false;
    let hasTotalError = false;
    
    if (!rmpmChecked) {
      
      const finalErrors = { ...inputErrors };
      // Add all row-level errors
      newInventory.forEach((item, idx) => {
        const qty = parseFloat(item.plannedAllocatedQuantity || 0);
        if (qty > 0) {
          const netQty = parseFloat(item.netAvailableQuantity || 0);
          if (qty > netQty) {
            finalErrors[idx] = `Planned Allocated Qty. exceeds Net Available Qty. for this material batch number.`;
          }
        }
      });
     
      if (errorMsg && !finalErrors[index]) {
        finalErrors[index] = errorMsg;
      }
      // If sum error exists, clear individual BOM errors
      if (totalErrorMsg) {
        Object.keys(finalErrors).forEach((key) => {
          if (finalErrors[key]?.includes("exceeds Material BoM Qty")) {
            delete finalErrors[key];
          }
        });
      }
      hasRowErrors = Object.values(finalErrors).some((msg) => msg && msg.length > 0);
      hasTotalError = totalErrorMsg.length > 0; // Only check sum errors for multiple allocation
    } else {
      // For single batch, use simple check - only check over/under allocation errors
      const updatedErrors = { ...inputErrors, [index]: errorMsg };
      hasRowErrors = Object.values(updatedErrors).some((msg) => msg && msg.length > 0);
      // No sum errors for single batch allocation
      hasTotalError = false;
    }
    
    if (hasRowErrors || hasTotalError) {
      // Validation errors take precedence - keep save disabled
      setIsSaveEnabled(false);
      return;
    }

    // If no validation errors, proceed with normal save button logic
    const areAllLotNumberDetailsEmpty = lotAllocationData?.every(
      (item) => item?.lotNumberAllocationDetails?.length === 0
    );
    
    // Check if values are within over/under allocation limits (when enabled)
    let isWithinAllocationLimits = false;
    if (allowOverAllocation || allowUnderAllocation) {
      const enteredValuesCount = newInventory.filter(
        (item) => parseFloat(item.plannedAllocatedQuantity || 0) > 0
      ).length;
      
      if (enteredValuesCount > 0) {
        if (allowOverAllocation) {
          const maxAllowed = requiredQuantity + (overAllocationLimit * requiredQuantity / 100);
          isWithinAllocationLimits = parseFloat(totalEnteredQuantity.toFixed(3)) >= parseFloat(requiredQuantity.toFixed(3)) && 
                                      parseFloat(totalEnteredQuantity.toFixed(3)) <= parseFloat(maxAllowed.toFixed(3));
        } else if (allowUnderAllocation) {
          const minAllowed = requiredQuantity - (underAllocationLimit * requiredQuantity / 100);
          isWithinAllocationLimits = parseFloat(totalEnteredQuantity.toFixed(3)) >= parseFloat(minAllowed.toFixed(3)) && 
                                      parseFloat(totalEnteredQuantity.toFixed(3)) <= parseFloat(requiredQuantity.toFixed(3));
        }
      }
    }
    
    // If checkbox is checked, only validate comment 
    if (!rmpmChecked) {
     
      const isValidAllocation = isExactMatch || isWithinAllocationLimits;
      
      setIsSaveEnabled(
        areAllLotNumberDetailsEmpty
          // ? isValidAllocation ||
          //     selectedReason.trim() !== "" ||
          //     comment.trim() !== ""
          // : isValidAllocation &&
          //     ((selectedReason !== "" && selectedReason !== "EXMRP019") ||
          //       (selectedReason === "EXMRP019" && comment.trim() !== ""))
          ? isValidAllocation || comment.trim() !== ""
          : isValidAllocation && comment.trim() !== ""
      );
    } else if (rmpmChecked) {
      //setIsSaveEnabled(selectedReason.trim() !== "" || comment.trim() !== "");
      setIsSaveEnabled(comment.trim() !== "");
    } else {
      setIsSaveEnabled(
        //isExactMatch && (selectedReason.trim() !== "" || comment.trim() !== "")
        isExactMatch && comment.trim() !== ""
      );
    }
  };

  // Handle Save
  const handleSave = () => {
    let lotNumberAllocationDetails = [];

    if (rmpmChecked) {
     
      if (selectedLot && allowOverAllocation) {
       
        const selectedLotItem = updatedInventory.find(item => lotRowKey(item) === selectedLot);
        const userEnteredQty = selectedLotItem?.plannedAllocatedQuantity 
          ? parseFloat(selectedLotItem.plannedAllocatedQuantity) 
          : parseFloat(selectedLotDataState?.boNetAvailableQuantity || 0);
        
        let convertedValue = userEnteredQty;
        if (lotData?.componentAllocationDetails?.conversionOperator == "=") {
          convertedValue = userEnteredQty;
        } else if (lotData?.componentAllocationDetails?.conversionOperator == "/") {
          convertedValue = userEnteredQty / lotData?.componentAllocationDetails?.conversionConstant;
        } else if (lotData?.componentAllocationDetails?.conversionOperator == "*") {
          convertedValue = userEnteredQty * lotData?.componentAllocationDetails?.conversionConstant;
        }

        // Find the index of the selected lot to get its deplete instruction checkbox state
        const selectedLotIndex = updatedInventory.findIndex(
          (item) => lotRowKey(item) === selectedLot
        );
        lotNumberAllocationDetails = [
          {
            plannedAllocatedQuantity: localStorage.collabType === "CMO" ? convertedValue : userEnteredQty,
            lotNumber: selectedLotDataState?.boInventoryBatchNumber,
            cmoLotNumber: selectedLotDataState?.cmoInventoryBatchNumber,
            // Include whether user wants to add deplete instruction for this batch
            isDepletedBatch: depleteInstructions[selectedLotIndex] || false,
            // BCM-234 F9: carry the source storage location so Corda can decide
            // whether to defer the SAP push. "" / null = Special Stocks with Vendor.
            storageLocation: selectedLotDataState?.storageLocationID ?? "",
          },
        ];
      } else if (selectedLot && allowUnderAllocation) {
        
        const selectedLotItem = updatedInventory.find(item => lotRowKey(item) === selectedLot);
        const userEnteredQty = selectedLotItem?.plannedAllocatedQuantity 
          ? parseFloat(selectedLotItem.plannedAllocatedQuantity) 
          : parseFloat(selectedLotDataState?.boNetAvailableQuantity || 0);
        
        let convertedValue = userEnteredQty;
        if (lotData?.componentAllocationDetails?.conversionOperator == "=") {
          convertedValue = userEnteredQty;
        } else if (lotData?.componentAllocationDetails?.conversionOperator == "/") {
          convertedValue = userEnteredQty / lotData?.componentAllocationDetails?.conversionConstant;
        } else if (lotData?.componentAllocationDetails?.conversionOperator == "*") {
          convertedValue = userEnteredQty * lotData?.componentAllocationDetails?.conversionConstant;
        }

        // Find the index of the selected lot to get its deplete instruction checkbox state
        const selectedLotIndex = updatedInventory.findIndex(
          (item) => lotRowKey(item) === selectedLot
        );
        lotNumberAllocationDetails = [
          {
            plannedAllocatedQuantity: localStorage.collabType === "CMO" ? convertedValue : userEnteredQty,
            lotNumber: selectedLotDataState?.boInventoryBatchNumber,
            cmoLotNumber: selectedLotDataState?.cmoInventoryBatchNumber,
            // Include whether user wants to add deplete instruction for this batch
            isDepletedBatch: depleteInstructions[selectedLotIndex] || false,
            // BCM-234 F9: carry the source storage location so Corda can decide
            // whether to defer the SAP push. "" / null = Special Stocks with Vendor.
            storageLocation: selectedLotDataState?.storageLocationID ?? "",
          },
        ];
      } else if (selectedLot) {
        // Find the index of the selected lot to get its deplete instruction checkbox state
        const selectedLotIndex = updatedInventory.findIndex(
          (item) => lotRowKey(item) === selectedLot
        );
        const isDepleted = depleteInstructions[selectedLotIndex] || false;
        // When the row has a deplete instruction, use the value already stored for
        // it (set to Net Available Qty. by the deplete checkbox handler) instead of
        // the BOM qty, since that's the value the UI displayed and locked the
        // field to.
        const selectedLotItem = updatedInventory.find(
          (item) => lotRowKey(item) === selectedLot
        );
        const plannedQty = isDepleted
          ? parseFloat(selectedLotItem?.plannedAllocatedQuantity || 0)
          : parseFloat(lotData?.componentAllocationDetails?.boComponentQuantity || 0);
        lotNumberAllocationDetails = [
          {
            plannedAllocatedQuantity: plannedQty,
            lotNumber: selectedLotDataState?.boInventoryBatchNumber,
            cmoLotNumber: selectedLotDataState?.cmoInventoryBatchNumber,
            // Include whether user wants to add deplete instruction for this batch
            isDepletedBatch: isDepleted,
            // BCM-234 F9: carry the source storage location so Corda can decide
            // whether to defer the SAP push. "" / null = Special Stocks with Vendor.
            storageLocation: selectedLotDataState?.storageLocationID ?? "",
          },
        ];
      }
    } else {
      // Case 1: User Entered Values in Textboxes
      lotNumberAllocationDetails = updatedInventory
        .filter((item) => parseFloat(item.plannedAllocatedQuantity) > 0) // Only include filled textboxes
        .map((item, itemIndex) => {
          const userValue = parseFloat(item.plannedAllocatedQuantity);
          let convertedValue = userValue;

          if (lotData?.componentAllocationDetails?.conversionOperator == "=") {
            convertedValue = userValue;
          } else if (lotData?.componentAllocationDetails?.conversionOperator == "/") {
            convertedValue = userValue / lotData?.componentAllocationDetails?.conversionConstant;
          } else if (lotData?.componentAllocationDetails?.conversionOperator == "*") {
            convertedValue = userValue * lotData?.componentAllocationDetails?.conversionConstant;
          }

          // Find the original index in updatedInventory to get the deplete instruction checkbox state
          const originalIndex = updatedInventory.findIndex(
            (invItem) => lotRowKey(invItem) === lotRowKey(item)
          );

          return {
            plannedAllocatedQuantity: localStorage.collabType === "CMO" ? convertedValue : userValue,
          // lotNumber: item.inventoryBatchNumber,
            lotNumber: item.boInventoryBatchNumber,
            cmoLotNumber: item?.cmoInventoryBatchNumber,
            // Include whether user wants to add deplete instruction for this batch
            isDepletedBatch: depleteInstructions[originalIndex] || false,
            // BCM-234 F9: carry the source storage location so Corda can decide
            // whether to defer the SAP push. "" / null = Special Stocks with Vendor.
            storageLocation: item?.storageLocationID ?? "",
          };
        });
    }

    // Include Previous Lot Allocations from lotData
    const previousLotNumberAllocationDetails =
      lotData?.lotNumberAllocationDetails?.map((item) => ({
        // lotNumber: item.lotNumber,
        // plannedAllocatedQuantity: item.plannedAllocatedQuantity,
        lotNumber: localStorage.collabType === "CMO" ? item?.boLotNumber : item?.lotNumber,
        plannedAllocatedQuantity: localStorage.collabType === "CMO" ? item?.boPlannedAllocatedQuantity : item.plannedAllocatedQuantity,
      })) || [];


    // Create Final Payload Object
    const savePayload = {
      schrockenSLID: schrockenSLID,
      lastUpdatedBy: "",
      senderLegalName: "",
      receiverLegalName: "",
      dateChangeFlag: false,
      cdmoOrgID: vendorMosymphonyID,
      // allowOverAllocation: allowOverAllocation,
      overAllocationDone: allowOverAllocation,
      underAllocationDone: allowUnderAllocation,
      // Allocation warning flags (BCM-613) — true only when the saved total sits
      // inside the configured warning band (the same condition that displayed the
      // amber warning). Corda builds the transaction with the dedicated
      // Over/UnderAllocationDoneWithinWarningLimit command → PO-history event.
      overAllocationDoneWithinWarningLimit: allocationWarning.type === 'over',
      underAllocationDoneWithinWarningLimit: allocationWarning.type === 'under',
      poNumber: poData?.scheduleLineDetails?.scheduleAgreementNumber,
      poStatus: poData?.scheduleLineDetails?.scheduleLineStatusText,
      productCodeDiscription: `${poData?.scheduleLineDetails?.fgMaterialCode} / ${poData?.scheduleLineDetails?.fgMaterialName}`,
      bomQty: poData?.scheduleLineDetails?.targetQuantity,
      uom: poData?.scheduleLineDetails?.targetQuantityUOM,
      boProductCode: lotAllocationRes?.boProductCode,
      boProductDescription: lotAllocationRes?.boProductDescription,
      cmoProductCode: lotAllocationRes?.cmoProductCode,
      cmoProductDescription: lotAllocationRes?.cmoProductDescription,
      singleAllocation: rmpmChecked,
      multipleAllocation: !rmpmChecked,
      allocationChanged: previousLotNumberAllocationDetails.length > 0,
      
      componentAllocationDataList: [
        {
          componentAllocationDetails: {
            componentCode:
              localStorage.collabType === "CMO"
                ? lotData?.componentAllocationDetails?.boComponentCode
                : lotData?.componentAllocationDetails?.componentCode,
            deliveryScheduleLineCounter:
              lotData?.componentAllocationDetails?.deliveryScheduleLineCounter,
            reasonCode: selectedReason,
            comment: comment.trim(),
            boComponentCode: lotData?.componentAllocationDetails?.boComponentCode,
            boComponentName: lotData?.componentAllocationDetails?.boComponentName,
            cmoComponentCode: lotData?.componentAllocationDetails?.cmoComponentCode,
            cmoComponentName: lotData?.componentAllocationDetails?.cmoComponentName,
          },
          previousLotNumberAllocationDetails,
          lotNumberAllocationDetails,
        },
      ],
    };




    dispatch({
      type: OPEN_BACKDROP,
      backdropText: "Please wait....",
    });
    axios
      .post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateComponentAllocationNew`,
        savePayload
      )
      .then((res) => {
        if (res.data.status) {
          dispatch({
            type: DESTROY_BACKDROP,
          });

          handleClose("success");
        } else {
          handleClose("failed");
          dispatch({
            type: DESTROY_BACKDROP,
          });
        }
      })
      .catch((err) => {
        handleClose("failed");
        dispatch({
          type: DESTROY_BACKDROP,
        });
        // console.log(err);
        // setError(true);
      })
      .finally(() => {
        onClose();
      });
  };

  useEffect(() => {
    const inventory = Array.isArray(lotData?.inventoryDetails)
      ? [...lotData.inventoryDetails]
      : [];
    setUpdatedInventory(inventory);
    // Initialize deplete instructions - all unchecked by default
    const initialDepleteInstructions = {};
    inventory.forEach((item, index) => {
      initialDepleteInstructions[index] = false;
    });
    setDepleteInstructions(initialDepleteInstructions);
  }, [lotData]);
  const getOrganizationIdFromToken = () => {
      const authToken = localStorage.getItem("authToken"); // Get token from localStorage
      if (!authToken) return null; // Return null if no token is found
  
      try {
        const decodedToken = jwtDecode(authToken); // Decode the JWT token
        return decodedToken.collaborator_type_id || null; // Return collaborator_type_id or null if not found
      } catch (error) {
        console.error("Error decoding JWT token:", error);
        return null;
      }
    };
const collaboratorTypeID = getOrganizationIdFromToken();

  // useEffect(() => {
  //   const requestObject = {
  //     searchField: "all",
  //     searchValue: "",
  //     reasonCodeGroupType: "all",
  //     reasonCodeType: "all",
  //     status: "all",
  //     collaboratorTypeID: "all"

  //   };
  //   axios
  //     .post(
  //       `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchReasonCodes`,
  //       requestObject
  //     )
  //     .then((res) => {
  //       if (res.data.status) {
  //         if (res.data.data) {
  //           setReasonCodes(res.data.data.reasonCodeList);
  //         }
  //       }
  //     })
  //     .catch((err) => {
  //       console.log(err);
  //     });
  // }, []);


  // Ensure rmpmChecked is always true if allowMultipleLotAllocation is false
  useEffect(() => {
    if (
      Array.isArray(lotAllocationData) &&
      lotAllocationData.length > 0 &&
      Array.isArray(lotAllocationData[0].lotNumberAllocationDetails) &&
      lotAllocationData[0].lotNumberAllocationDetails.length > 0 &&
      lotAllocationData[0].lotNumberAllocationDetails[0]?.allowMultipleLotAllocation
    ) {
      setrmpmChecked(false);
    } else {
      setrmpmChecked(true);
    }
  }, [lotAllocationData]);




  // Initialize save button state when component loads
  useEffect(() => {
   
    const hasErrors = Object.values(inputErrors).some((msg) => msg && msg.length > 0) || 
                      (totalAllocationError && totalAllocationError.length > 0);
    
    if (hasErrors) {
      setIsSaveEnabled(false);
      return;
    }

    // Check if lotNumberAllocationDetails exists but has no plannedAllocatedQuantity
    const hasLotDetailsWithoutPlannedQty = 
      Array.isArray(lotData?.lotNumberAllocationDetails) &&
      lotData.lotNumberAllocationDetails.length > 0 &&
      !lotData.lotNumberAllocationDetails.some(lot => lot.hasOwnProperty('plannedAllocatedQuantity'));

    const isLotDetailsEmpty =
      Array.isArray(lotData?.lotNumberAllocationDetails) &&
      lotData?.lotNumberAllocationDetails.length === 0;

    if (hasLotDetailsWithoutPlannedQty || isLotDetailsEmpty) {
      
      if (rmpmChecked) {
        // For single batch mode: check if value is entered when over/under allocation is enabled
        if (allowOverAllocation || allowUnderAllocation) {
          const selectedLotItem = updatedInventory?.find(item => lotRowKey(item) === selectedLot) || null;
          const enteredPlannedQty = selectedLotItem ? parseFloat(selectedLotItem.plannedAllocatedQuantity || 0) : 0;
          const hasEnteredValue = enteredPlannedQty > 0;
          setIsSaveEnabled(selectedLot !== null && hasEnteredValue);
        } else {
          // No over/under allocation: value is auto-set, just need radio selection
          setIsSaveEnabled(selectedLot !== null);
        }
      } else {
        // For text input mode, check exact match or within allocation limits
        const requiredQuantity = parseFloat(
          lotData?.componentAllocationDetails?.componentQuantity || 0
        );
        const totalEnteredQuantity = (updatedInventory || []).reduce(
          (acc, item) => acc + (parseFloat(item.plannedAllocatedQuantity) || 0),
          0
        );
        const isExactMatch =
          totalEnteredQuantity.toFixed(3) === requiredQuantity.toFixed(3);
        
        // Check if values are within over/under allocation limits (when enabled)
        let isWithinAllocationLimits = false;
        if (allowOverAllocation || allowUnderAllocation) {
          const enteredValuesCount = (updatedInventory || []).filter(
            (item) => parseFloat(item.plannedAllocatedQuantity || 0) > 0
          ).length;
          
          if (enteredValuesCount > 0) {
            if (allowOverAllocation) {
              const maxAllowed = requiredQuantity + (overAllocationLimit * requiredQuantity / 100);
              isWithinAllocationLimits = parseFloat(totalEnteredQuantity.toFixed(3)) >= parseFloat(requiredQuantity.toFixed(3)) && 
                                          parseFloat(totalEnteredQuantity.toFixed(3)) <= parseFloat(maxAllowed.toFixed(3));
            } else if (allowUnderAllocation) {
              const minAllowed = requiredQuantity - (underAllocationLimit * requiredQuantity / 100);
              isWithinAllocationLimits = parseFloat(totalEnteredQuantity.toFixed(3)) >= parseFloat(minAllowed.toFixed(3)) && 
                                          parseFloat(totalEnteredQuantity.toFixed(3)) <= parseFloat(requiredQuantity.toFixed(3));
            }
          }
        }
        
        setIsSaveEnabled(isExactMatch || isWithinAllocationLimits);
      }
    } else if (rmpmChecked) {
     
      const selectedLotItem = updatedInventory?.find(item => lotRowKey(item) === selectedLot) || null;
      const enteredPlannedQty = selectedLotItem ? parseFloat(selectedLotItem.plannedAllocatedQuantity || 0) : 0;
      const hasEnteredValue = enteredPlannedQty > 0;
      
     
      if (allowOverAllocation || allowUnderAllocation) {
        // Require radio selection, entered value, and comment (reason code dropdown is commented out)
        setIsSaveEnabled(
          selectedLot !== null &&
          hasEnteredValue &&
            // ((selectedReason.trim() !== "" &&
            //   selectedReason.trim() !== "EXMRP019") ||
            //   (selectedReason.trim() === "EXMRP019" &&
            //     comment.trim() !== "") ||
            //   (selectedReason.trim() === "" && comment.trim() !== ""))
            comment.trim() !== ""
        );
      } else {
        
        setIsSaveEnabled(
          selectedLot !== null &&
            // ((selectedReason.trim() !== "" &&
            //   selectedReason.trim() !== "EXMRP019") ||
            //   (selectedReason.trim() === "EXMRP019" &&
            //     comment.trim() !== "") ||
            //   (selectedReason.trim() === "" && comment.trim() !== ""))
            comment.trim() !== ""
        );
      }
    } else {
      // If checkbox is not checked, validate based on text inputs and require reason/comment
      const requiredQuantity = parseFloat(
        lotData?.componentAllocationDetails?.componentQuantity || 0
      );
      const totalEnteredQuantity = (updatedInventory || []).reduce(
        (acc, item) => acc + (parseFloat(item.plannedAllocatedQuantity) || 0),
        0
      );
      const isExactMatch =
        totalEnteredQuantity.toFixed(3) === requiredQuantity.toFixed(3);

      // Check if values are within over/under allocation limits (when enabled)
      let isWithinAllocationLimits = false;
      if (allowOverAllocation || allowUnderAllocation) {
        const enteredValuesCount = (updatedInventory || []).filter(
          (item) => parseFloat(item.plannedAllocatedQuantity || 0) > 0
        ).length;
        
        if (enteredValuesCount > 0) {
          if (allowOverAllocation) {
            const maxAllowed = requiredQuantity + (overAllocationLimit * requiredQuantity / 100);
            isWithinAllocationLimits = parseFloat(totalEnteredQuantity.toFixed(3)) >= parseFloat(requiredQuantity.toFixed(3)) && 
                                        parseFloat(totalEnteredQuantity.toFixed(3)) <= parseFloat(maxAllowed.toFixed(3));
          } else if (allowUnderAllocation) {
            const minAllowed = requiredQuantity - (underAllocationLimit * requiredQuantity / 100);
            isWithinAllocationLimits = parseFloat(totalEnteredQuantity.toFixed(3)) >= parseFloat(minAllowed.toFixed(3)) && 
                                        parseFloat(totalEnteredQuantity.toFixed(3)) <= parseFloat(requiredQuantity.toFixed(3));
          }
        }
      }
      
      const isValidAllocation = isExactMatch || isWithinAllocationLimits;

      const areAllLotNumberDetailsEmpty = lotAllocationData?.every(
        (item) => item?.lotNumberAllocationDetails?.length === 0
      );

      if (areAllLotNumberDetailsEmpty) {
        setIsSaveEnabled(
          // isValidAllocation ||
          //   selectedReason.trim() !== "" ||
          isValidAllocation || comment.trim() !== ""
        );
      } else {
        setIsSaveEnabled(
          isValidAllocation &&
            // ((selectedReason.trim() !== "" &&
            //   selectedReason.trim() !== "EXMRP019") ||
            //   (selectedReason.trim() === "EXMRP019" &&
            //     comment.trim() !== ""))
            comment.trim() !== ""
        );
      }
    }
  }, [selectedLot, selectedReason, comment, rmpmChecked, updatedInventory, lotData, lotAllocationData, inputErrors, totalAllocationError, allowOverAllocation, allowUnderAllocation, overAllocationLimit, underAllocationLimit]);


  return (
    <React.Fragment
    style={{
      // maxHeight: '80vh', // or 100% if you're controlling from parent
      overflow: 'auto',  // or 'hidden' to remove scrollbar
    }}>
     {!isClosing && <BackDropLoading />}
      {/* BCM-234 F9: suppress horizontal scroll at the dialog-body level so
          stray overflow from any single inner element (legend bar, wide
          inline note, etc.) doesn't create an extra scrollbar at the bottom. */}
      <DialogContent style={{ overflowX: "hidden" }}>
        <Grid container justifyContent="flex-end">
          <Typography
            variant="body2"
            style={{
              fontSize: "1rem",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => {
              onClose();
              setIsClosing(true);
              requestAnimationFrame(() => {
                onClose();
              });
            }}
          >
            Cancel <CloseIcon style={{ color: "#FF0000" }} fontSize="large" />
          </Typography>
        </Grid>
        <Typography
          style={{
            fontWeight: "700",
            color: "black",
            fontSize: "22px",
            marginBottom: "0.5rem",
            marginLeft: "1rem",
            marginRight: "1rem",
            textAlign: "center",
            position: "sticky",
    top: 0, 
    backgroundColor: "white",
    zIndex: 1000, 
    padding: "0.5rem"
          }}
        >
          Edit Material Planned Allocated Qty. for PO {" "}
          {poData?.scheduleLineDetails?.scheduleAgreementNumber}
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "#f2f8fb",
            border: "1px solid #ccc",
            borderRadius: "4px",
            // padding: "3px",
          }}
        >
          <Table className={classes.tablepadding} style={{backgroundColor: "#f2f8fb"}}>
            {/* <TableBody>
              <TableRow style={{ backgroundColor: "#f2f8fb" }}>
                <TableCell>
                  <Typography className={classes.updatedHeader}>
                    CMO/CDMO
                  </Typography>
                  <Typography className={classes.updatedSubHeader}>
                    {poData?.scheduleLineDetails?.vendorName}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography className={classes.updatedHeader}>
                    Plant ID
                  </Typography>
                  <Typography className={classes.updatedSubHeader}>
                    {presentFormatedData(
                      poData?.scheduleLineDetails?.vendorPlant
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography style={{ fontWeight: "bold" }}>
                    Storage Location ID
                  </Typography>
                  <Typography style={{ maxWidth: 250 }}
                    className={classes.updatedSubHeader}>
                    {presentFormatedData(
                      poData?.scheduleLineDetails?.storageLocation
                    )}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography className={classes.updatedHeader}>
                    Material code / description
                  </Typography>
                  <Typography
                    style={{ maxWidth: 250 }}
                    className={classes.updatedSubHeader}
                  >
                    {presentFormatedData(
                      lotData?.componentAllocationDetails?.componentCode
                    )}
                    /
                    {presentFormatedData(
                      lotData?.componentAllocationDetails?.componentName
                    )}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography className={classes.updatedHeader}>
                    Material BOM qty.
                  </Typography>
                  <Typography className={classes.updatedSubHeader}>
                    {lotData?.componentAllocationDetails?.componentQuantity}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography className={classes.updatedHeader}>UOM</Typography>
                  <Typography className={classes.updatedSubHeader}>
                    {lotData?.componentAllocationDetails?.uom}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody> */}

            <TableHead>
              <TableRow style={{ maxHeight: "2rem" }}>
                <TableCell style={{ border: "none" }}>
                  <Typography className={classes.updatedHeader}>
                    {localStorage.collabType === "CMO" ? "Sponsor" : "CMO"}
                  </Typography>
                </TableCell>
                <TableCell style={{ border: "none" }}>
                  <Typography className={classes.updatedHeader}>
                    Plant ID
                  </Typography>
                </TableCell>
                {/* BCM-234: Storage Location ID column removed from the blue
                    ribbon — storage location is now shown per row in the
                    inventory table below, not at the PO level. */}
                <TableCell style={{ border: "none" }}>
                  <Typography className={classes.updatedHeader}>
                  BMS Material Code / Description
                  </Typography>
                </TableCell>
                <TableCell style={{ border: "none" }}>
                  <Typography className={classes.updatedHeader}>
                    Material BOM Qty.
                  </Typography>
                </TableCell>
                <TableCell style={{ border: "none" }}>
                  <Typography className={classes.updatedHeader}>UOM</Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow
                style={{
                  backgroundColor: "#f2f8fb",
                  border: "none",
                  maxHeight: "2rem",
                }}
              >
                <TableCell style={{ border: "none" }}>
                  <Typography className={classes.updatedSubHeader}>
                    {localStorage.collabType === "CMO" 
                      ? poData?.scheduleLineDetails?.sponsorName 
                      : poData?.scheduleLineDetails?.vendorName}
                  </Typography>
                </TableCell>
                <TableCell style={{ border: "none" }}>
                  <Typography className={classes.updatedSubHeader}>
                    {presentFormatedData(
                      poData?.scheduleLineDetails?.vendorPlant
                    )}
                  </Typography>
                </TableCell>
                {/* BCM-234: Storage Location ID body cell removed from the
                    blue ribbon to match the header removal above. */}
                <TableCell style={{ border: "none" }}>
                  <Typography
                    style={{ maxWidth: 250 }}
                    className={classes.updatedSubHeader}
                  >
                    {presentFormatedData(
                      lotData?.componentAllocationDetails?.componentCode
                    )}
                    /
                    {presentFormatedData(
                      lotData?.componentAllocationDetails?.componentName
                    )}
                  </Typography>
                </TableCell>
                <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedSubHeader}>
                      {lotData?.componentAllocationDetails?.componentQuantity !== undefined || lotData?.componentAllocationDetails?.componentQuantity !== null
                        ? Number(lotData?.componentAllocationDetails?.componentQuantity).toFixed(3)
                        : ""}
                    </Typography>
                </TableCell>
                <TableCell style={{ border: "none" }}>
                  <Typography className={classes.updatedSubHeader}>
                    {lotData?.componentAllocationDetails?.uom}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: "flex", width: '100%', gap: '16px', marginTop: "16px", marginBottom: "16px", justifyContent: 'space-between', flexWrap: 'wrap' }}>
          {/* "Show batches from other storage locations for Plant <X>" checkbox.
              Default unchecked -> only Special-Stocks-with-Vendor rows are listed (existing behaviour).
              Checked -> all eligible batches across the plant's other storage locations are listed
              in addition. The label dynamically reflects the PO's Plant ID. */}
          <Box
            sx={{
              border: "2px solid #ccc",
              borderRadius: 0,
              padding: "8px 24px",
              display: "flex",
              alignItems: "center",
              height: "auto",
              backgroundColor: "#fff",
              flex: 1,
              minWidth: '300px',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOtherStorageLocations}
                  onChange={(e) => setShowOtherStorageLocations(e.target.checked)}
                  classes={{ checked: classes.customCheckbox }}
                />
              }
              label={
                <span style={{ fontSize: '13px' }}>
                  {`Show batches from other storage locations for Plant ${
                    poData?.scheduleLineDetails?.vendorPlant || ""
                  }`}
                </span>
              }
            />
          </Box>
          <Box
            sx={{
              border: "2px solid #ccc",
              borderRadius: 0,
              padding: "8px 24px",
              display: "flex",
              alignItems: "center",
              height: "auto",
              backgroundColor: "#fff",
              flex: "0 1 auto",
              minWidth: 'auto',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rmpmChecked}
                  onChange={(e) => {
                    // Clear all errors when switching modes
                    setInputErrors({});
                    setTotalAllocationError("");
                    setErrorInfo("");
                    
                  
                    const resetInventory = (lotData?.inventoryDetails && Array.isArray(lotData.inventoryDetails))
                      ? lotData.inventoryDetails.map(item => ({
                          ...item,
                          plannedAllocatedQuantity: "",
                          disabled: false
                        }))
                      : [];
                    setUpdatedInventory(resetInventory);
                    // Reset deplete instruction checkboxes - uncheck all when mode changes
                    const resetDepleteInstructions = {};
                    resetInventory.forEach((item, idx) => {
                      resetDepleteInstructions[idx] = false;
                    });
                    setDepleteInstructions(resetDepleteInstructions);

                    // Reset selected lot and related state when switching modes
                    setSelectedLot(null);
                    setNetAvailableQty(null);
                    setSelectedLotDataState(null);
                    
                    setrmpmChecked(e.target.checked);

                    // Ensure a radio button is selected if checkbox is checked
                    if (e.target.checked) {
                      // Check if lotNumberAllocationDetails exists but has no plannedAllocatedQuantity
                      const hasLotDetailsWithoutPlannedQty = 
                        Array.isArray(lotData?.lotNumberAllocationDetails) &&
                        lotData.lotNumberAllocationDetails.length > 0 &&
                        !lotData.lotNumberAllocationDetails.some(lot => lot.hasOwnProperty('plannedAllocatedQuantity'));

                      const isLotDetailsEmpty =
                        Array.isArray(lotData?.lotNumberAllocationDetails) &&
                        lotData?.lotNumberAllocationDetails.length === 0;

                      
                      setIsSaveEnabled(false);
                    } else {
                      const requiredQuantity = parseFloat(
                        lotData?.componentAllocationDetails?.componentQuantity ||
                          0
                      );
                      const totalEnteredQuantity = updatedInventory.reduce(
                        (acc, item) =>
                          acc + (parseFloat(item.plannedAllocatedQuantity) || 0),
                        0
                      );
                      const isExactMatch =
                        totalEnteredQuantity.toFixed(3) ===
                        requiredQuantity.toFixed(3);

                      const areAllLotNumberDetailsEmpty = lotAllocationData?.every(
                        (item) => item?.lotNumberAllocationDetails?.length === 0
                      );

                      if (areAllLotNumberDetailsEmpty) {
                        setIsSaveEnabled(
                          // isExactMatch ||
                          //   selectedReason.trim() !== "" ||
                          //   comment.trim() !== ""
                          isExactMatch || comment.trim() !== ""
                        );
                      } else {
                        setIsSaveEnabled(
                          isExactMatch &&
                            // ((selectedReason.trim() !== "" &&
                            //   selectedReason.trim() !== "EXMRP019") ||
                            //   (selectedReason.trim() === "EXMRP019" &&
                            //     comment.trim() !== ""))
                            comment.trim() !== ""
                        );
                      }
                    }
                  }}
                  classes={{ checked: classes.customCheckbox }}
                  disabled={!allowMultipleLotAllocation}
                />
              }
              label={<span style={{ fontSize: '13px' }}>Allocate single batch</span>}
            />
          </Box>
          <Box
            sx={{
              border: "2px solid #ccc",
              borderRadius: 0,
              padding: "8px 24px",
              display: "flex",
              alignItems: "center",
              height: "auto",
              backgroundColor: "#fff",
              flex: 1,
              gap: "24px",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowOverAllocation}
                  onChange={(e) => {
                    setAllowOverAllocation(e.target.checked);
                    if (e.target.checked) {
                      setAllowUnderAllocation(false); // Mutually exclusive
                    }
                    
                    
                    setErrorInfo("");
                    setInputErrors({});
                    setTotalAllocationError("");
                    setDisableReasonCode(false);
                    
                  
                    const resetInventory = (lotData?.inventoryDetails && Array.isArray(lotData.inventoryDetails))
                      ? lotData.inventoryDetails.map(item => ({
                          ...item,
                          plannedAllocatedQuantity: "",
                          disabled: false
                        }))
                      : [];
                    setUpdatedInventory(resetInventory);
                    // Reset deplete instruction checkboxes - uncheck all when mode changes
                    const resetDepleteInstructions = {};
                    resetInventory.forEach((item, idx) => {
                      resetDepleteInstructions[idx] = false;
                    });
                    setDepleteInstructions(resetDepleteInstructions);

                    // Clear selection (selected lot)
                    setSelectedLot(null);
                    setNetAvailableQty(null);
                    setSelectedLotDataState(null);
                    
                    // After clearing values and selection, save should be disabled
                    // Save will be enabled via useEffect when user selects radio and enters value
                    setIsSaveEnabled(false);
                    }}
                  classes={{ checked: classes.customCheckbox }}
                  disabled={!(
                    Array.isArray(lotAllocationData) &&
                    lotAllocationData.length > 0 &&
                    Array.isArray(lotAllocationData[0].lotNumberAllocationDetails) &&
                    lotAllocationData[0].lotNumberAllocationDetails.length > 0 &&
                    lotAllocationData[0].lotNumberAllocationDetails[0].allowOverAllocation
                  )}
                />
              }
              label={<span style={{ fontSize: '13px' }}>Do over allocation</span>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowUnderAllocation}
                  onChange={(e) => {
                    setAllowUnderAllocation(e.target.checked);
                    if (e.target.checked) {
                      setAllowOverAllocation(false); // Mutually exclusive
                    }
                    
                   
                    setErrorInfo("");
                    setInputErrors({});
                    setTotalAllocationError("");
                    setDisableReasonCode(false);
                    
                 
                    const resetInventory = (lotData?.inventoryDetails && Array.isArray(lotData.inventoryDetails))
                      ? lotData.inventoryDetails.map(item => ({
                          ...item,
                          plannedAllocatedQuantity: "",
                          disabled: false
                        }))
                      : [];
                    setUpdatedInventory(resetInventory);
                    // Reset deplete instruction checkboxes - uncheck all when mode changes
                    const resetDepleteInstructions = {};
                    resetInventory.forEach((item, idx) => {
                      resetDepleteInstructions[idx] = false;
                    });
                    setDepleteInstructions(resetDepleteInstructions);

                    // Clear selection (selected lot)
                    setSelectedLot(null);
                    setNetAvailableQty(null);
                    setSelectedLotDataState(null);
                    
                    // After clearing values and selection, save should be disabled
                    // Save will be enabled via useEffect when user selects radio and enters value
                    setIsSaveEnabled(false);
                    }}
                  classes={{ checked: classes.customCheckbox }}
                  disabled={!(
                    Array.isArray(lotAllocationData) &&
                    lotAllocationData.length > 0 &&
                    Array.isArray(lotAllocationData[0].lotNumberAllocationDetails) &&
                    lotAllocationData[0].lotNumberAllocationDetails.length > 0 &&
                    (lotAllocationData[0].lotNumberAllocationDetails[0].allowUnderAllocation ||
                     lotAllocationData[0].lotNumberAllocationDetails[0].allowUnderAllocation === true)
                  )}
                />
              }
              label={<span style={{ fontSize: '13px' }}>Do under allocation</span>}
            />
          </Box>
        </Box>

        <TableContainer
          component={Paper}
          className={classes.updatedTableContainer}
        >
          <Table className={classes.secondTablepadding} stickyHeader>
            <TableHead>
              <TableRow>
                {rmpmChecked && (
                  <TableCell align="center" className={classes.headerCell}>
                    Selection
                  </TableCell>
                )}
                {/* {localStorage?.collabType === "CMO" && <TableCell className={classes.headerCell}>
                  CMO Material Lot Number
                </TableCell>} */}
                {/* BCM-234 F9: Storage Location ID — second column.
                    Shows "Special Stocks with Vendor" for rows where the
                    inventory record carries no SLoc (storageLocationID 0/empty/null);
                    otherwise the actual SLoc ID (e.g. 0002, 0006). */}
                <TableCell className={classes.headerCell} style={{ paddingLeft: '8px' }}>
                  Storage Location ID
                </TableCell>
                <TableCell className={classes.headerCell} style={{ paddingLeft: '8px' }}>
                 BMS Material Batch Number
                </TableCell>
                <TableCell className={classes.headerCell}>
                  Net Available Qty.
                </TableCell>
                {/* BCM-234: On-Hand Qty. column (onHandQuantity from the same
                    fetchComponentAllocationForPOLine API). */}
                <TableCell className={classes.headerCell}>
                  On-Hand Qty.
                </TableCell>
                {/* Date of Manufacturing — display only, sourced from the batch record. */}
                <TableCell className={classes.headerCell}>
                  Date of Manufacturing
                </TableCell>
                <TableCell className={classes.headerCell}>
                  Expiry Date
                </TableCell>
                <TableCell className={classes.headerCell}>
                  Planned Allocated Qty.
                </TableCell>
                <TableCell className={classes.headerCell} align="center">
                  Add Deplete Instruction
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(updatedInventory?.length > 0
                ? updatedInventory
                : lotData?.inventoryDetails || []
              ).map((item, index) => {
                // BCM-234 F9: hide non-Special-Stocks-with-Vendor rows when the
                // checkbox is unchecked. Returning null keeps the underlying
                // index intact so per-row state (inputErrors, depleteInstructions,
                // updatedInventory[index]) keeps working unchanged.
                if (
                  !showOtherStorageLocations &&
                  !isSpecialStockWithVendor(item?.storageLocationID)
                ) {
                  return null;
                }
                return (
                <TableRow key={index}>
                  {rmpmChecked && (
                    <TableCell align="center">
                      <Radio
                        value={lotRowKey(item)}
                        checked={selectedLot === lotRowKey(item)}
                        onChange={(e) => {
                          // Clear previously selected radio button's entered value when switching in single batch mode with over/under allocation
                          const componentQuantity = parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0);
                          let newInventory = [...updatedInventory];
                          
                          if (rmpmChecked && (allowOverAllocation || allowUnderAllocation)) {
                            // In single batch mode with over/under allocation, clear previous selection's value
                            if (selectedLot && selectedLot !== e.target.value) {
                              newInventory = updatedInventory.map((lotItem) => {
                                if (lotRowKey(lotItem) === selectedLot) {
                                  // Clear the previously selected lot's entered value
                                  return {
                                    ...lotItem,
                                    plannedAllocatedQuantity: "",
                                  };
                                }
                                return lotItem;
                              });
                              setUpdatedInventory(newInventory);
                            }
                          }
                          
                          setSelectedLot(e.target.value);

                          // Reset all deplete instruction checkboxes when selection changes in single batch mode
                          const resetDepleteInstructions = {};
                          updatedInventory.forEach((_, idx) => {
                            resetDepleteInstructions[idx] = false;
                          });
                          setDepleteInstructions(resetDepleteInstructions);

                          // Get the selected lot's net available quantity
                          const selectedLotData = (newInventory?.length > 0 ? newInventory : lotData?.inventoryDetails || [])
                          .find(lotItem => lotRowKey(lotItem) === e.target.value);
                          const selectedNetAvailableQty = parseFloat(selectedLotData?.netAvailableQuantity || 0);
                          
                          setNetAvailableQty(parseFloat(selectedLotData?.netAvailableQuantity || 0))
                          setSelectedLotDataState(selectedLotData || null);
                          
                          // When single batch is checked without over/under allocation, set planned allocated qty to Material BoM Qty for selected lot only
                          if (rmpmChecked && !allowUnderAllocation && !allowOverAllocation) {
                            const finalInventory = newInventory.map((lotItem) => {
                              if (lotRowKey(lotItem) === e.target.value) {
                                // Set Material BoM Qty for the newly selected lot
                                return {
                                  ...lotItem,
                                  plannedAllocatedQuantity: componentQuantity.toFixed(3),
                                };
                              } else {
                                // Clear planned allocated qty for all other lots
                                return {
                                  ...lotItem,
                                  plannedAllocatedQuantity: "",
                                };
                              }
                            });
                            setUpdatedInventory(finalInventory);
                          }
                          
                          // Clear errors - validation will happen when user enters planned allocated quantity
                          setErrorInfo("");
                          setInputErrors({});
                          setTotalAllocationError("");
                          setDisableReasonCode(false);

                          // Check if lotNumberAllocationDetails exists but has no plannedAllocatedQuantity
                          const hasLotDetailsWithoutPlannedQty = 
                            Array.isArray(lotData?.lotNumberAllocationDetails) &&
                            lotData.lotNumberAllocationDetails.length > 0 &&
                            !lotData.lotNumberAllocationDetails.some(lot => lot.hasOwnProperty('plannedAllocatedQuantity'));

                          const isLotDetailsEmpty =
                            Array.isArray(lotData?.lotNumberAllocationDetails) &&
                            lotData?.lotNumberAllocationDetails.length === 0;

              

                          // Get the selected lot's entered planned allocated quantity value
                          const selectedLotItem = newInventory?.find(item => lotRowKey(item) === e.target.value) ||
                                                   updatedInventory?.find(item => lotRowKey(item) === e.target.value) ||
                                                   null;
                          const enteredPlannedQty = selectedLotItem ? parseFloat(selectedLotItem.plannedAllocatedQuantity || 0) : 0;
                          const hasEnteredValue = enteredPlannedQty > 0;

                          if (hasLotDetailsWithoutPlannedQty || isLotDetailsEmpty) {
                            
                            if (allowOverAllocation || allowUnderAllocation) {
                              setIsSaveEnabled(e.target.value !== "" && hasEnteredValue);
                            } else {
                              // When no over/under allocation, value is auto-set to BOM quantity, so just check radio selection
                              setIsSaveEnabled(e.target.value !== "");
                            }
                          } else {
                          
                            if (allowOverAllocation || allowUnderAllocation) {
                              setIsSaveEnabled(
                                e.target.value !== "" &&
                                hasEnteredValue &&
                                  // ((selectedReason.trim() !== "" &&
                                  //   selectedReason.trim() !== "EXMRP019") ||
                                  //   (selectedReason.trim() === "EXMRP019" &&
                                  //     comment.trim() !== "") ||
                                  //   (selectedReason.trim() === "" && comment.trim() !== ""))
                                  comment.trim() !== ""
                              );
                            } else {
                              // No over/under allocation: value is auto-set, just need radio and comment
                              setIsSaveEnabled(
                                e.target.value !== "" &&
                                  // ((selectedReason.trim() !== "" &&
                                  //   selectedReason.trim() !== "EXMRP019") ||
                                  //   (selectedReason.trim() === "EXMRP019" &&
                                  //     comment.trim() !== "") ||
                                  //   (selectedReason.trim() === "" && comment.trim() !== ""))
                                  comment.trim() !== ""
                              );
                            }
                          }
                        }}
                        className={
                          selectedLot === lotRowKey(item)
                            ? classes.customRadio
                            : ""
                        }
                        disabled={(() => {
                          const netQty = parseFloat(item.netAvailableQuantity || 0);
                          const componentQty = parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0);
                          
                          // When single batch is checked without over/under allocation, only enable if Net Available Qty >= Material BoM Qty
                          if (rmpmChecked && !allowUnderAllocation && !allowOverAllocation) {
                            return netQty < componentQty;
                          }
                          
                          if (allowUnderAllocation) {
                            const minAllowed = componentQty - (underAllocationLimit * componentQty / 100);
                            return netQty < minAllowed;
                          }
                          
                          return netQty < componentQty;
                        })()}
                        style={{
                          opacity: (() => {
                            const netQty = parseFloat(item.netAvailableQuantity || 0);
                            const componentQty = parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0);
                            
                            // When single batch is checked without over/under allocation, only show if Net Available Qty >= Material BoM Qty
                            if (rmpmChecked && !allowUnderAllocation && !allowOverAllocation) {
                              return netQty < componentQty ? 0.5 : 1;
                            }
                            
                            if (allowUnderAllocation) {
                              const minAllowed = componentQty - (underAllocationLimit * componentQty / 100);
                              return netQty < minAllowed ? 0.5 : 1;
                            }
                            
                            return netQty < componentQty ? 0.5 : 1;
                          })(),
                          cursor: (() => {
                            const netQty = parseFloat(item.netAvailableQuantity || 0);
                            const componentQty = parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0);
                            
                            // When single batch is checked without over/under allocation, only enable if Net Available Qty >= Material BoM Qty
                            if (rmpmChecked && !allowUnderAllocation && !allowOverAllocation) {
                              return netQty < componentQty ? "not-allowed" : "pointer";
                            }
                            
                            if (allowUnderAllocation) {
                              const minAllowed = componentQty - (underAllocationLimit * componentQty / 100);
                              return netQty < minAllowed ? "not-allowed" : "pointer";
                            }
                            
                            return netQty < componentQty ? "not-allowed" : "pointer";
                          })(),
                        }}
                      />
                    </TableCell>
                  )}
                  {/* {localStorage?.collabType === "CMO" && <TableCell>{item?.cmoInventoryBatchNumber || 'N/A'}</TableCell>} */}
                  {/* BCM-234 F9: Storage Location ID cell. */}
                  <TableCell style={{ paddingLeft: '8px' }}>
                    {formatStorageLocation(item.storageLocationID)}
                  </TableCell>
                  <TableCell style={{ paddingLeft: '8px' }}>
                    {item.inventoryBatchNumber}
                    {item?.inventoryExpiryDate && new Date(item.inventoryExpiryDate) < new Date() ? (
                      <FlagIcon
                        style={{
                          color: "red",
                          fontSize: 28, // Small size
                          stroke: "black", // Outline color
                          strokeWidth: 1.5, // Adjust for thinness
                        }}
                      />
                    ) : item?.batchNearExpiry ? (
                      <FlagIcon
                        style={{
                          color: "yellow",
                          fontSize: 28, // Small size
                          stroke: "black", // Outline color
                          strokeWidth: 1.5, // Adjust for thinness
                        }}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {item.netAvailableQuantity !== undefined && item.netAvailableQuantity !== null
                      ? Number(item.netAvailableQuantity).toFixed(3)
                      : ""}
                  </TableCell>
                  {/* BCM-234: On-Hand Qty. — each row is an inventory record,
                      so onHandQuantity comes straight off item. */}
                  <TableCell>
                    {item.onHandQuantity !== undefined && item.onHandQuantity !== null && item.onHandQuantity !== ""
                      ? Number(item.onHandQuantity).toFixed(3)
                      : ""}
                  </TableCell>
                  {/* Date of Manufacturing — display only. */}
                  <TableCell>
                    {item.dateOfManufacturing
                      ? dateFormated(item.dateOfManufacturing, "DD-MMM-YYYY", "").toUpperCase()
                      : ""}
                  </TableCell>
                  <TableCell>
                    {dateFormated(
                      item.inventoryExpiryDate,
                      "DD-MMM-YYYY",
                      ""
                    ).toUpperCase()}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", padding: "0" }}>
                    <Box
                      sx={{
                        border: inputErrors[index] ? "2px solid #f44336" : "1px solid #ccc", // Red border when error
                        padding: "3px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        width: "80%",
                        backgroundColor: inputErrors[index] ? "#ffebee" : (() => {
                          // Locked (grey) while a deplete instruction is held.
                          if (depleteInstructions[index]) {
                            return "#e0e0e0";
                          }
                          if (rmpmChecked && !allowUnderAllocation && !allowOverAllocation) {
                            return "#e0e0e0";
                          }
                         
                          if (rmpmChecked && (allowOverAllocation || allowUnderAllocation)) {
                            return selectedLot !== lotRowKey(item) ? "#e0e0e0" : "transparent";
                          }
                          // In multi-batch mode (allocate single batch unchecked), keep container enabled
                          return "transparent";
                        })(),
                      }}
                    >
                      <TextField
                        variant="standard"
                        type="text"
                        inputProps={{
                          step: "0.001", // Allows decimals with up to 3 decimal places
                          min: "0",
                          pattern: "^[0-9]*\\.?[0-9]{0,3}$", // Allows numbers with up to 3 decimal places
                        }}
                        InputProps={{
                          disableUnderline: true,
                          style: {
                            padding: "2px",
                            fontSize: "14px",
                            backgroundColor: (() => {
                              // Highlight field with error (red background)
                              if (inputErrors[index]) {
                                return "#ffebee"; // Light red background for error
                              }
                              // Locked (grey) while a deplete instruction is held.
                              if (depleteInstructions[index]) {
                                return "#e0e0e0";
                              }
                              // Disabled state when single batch checked but over/under allocation not checked
                              if (rmpmChecked && !allowUnderAllocation && !allowOverAllocation) {
                                return "#e0e0e0"; // Gray background when disabled
                              }
                              
                              if (rmpmChecked && (allowOverAllocation || allowUnderAllocation)) {
                                return selectedLot !== lotRowKey(item) ? "#e0e0e0" : "white";
                              }
                              // In multi-batch mode (allocate single batch unchecked),
                              // always show enabled background; validation will still enforce limits.
                              const netQty = parseFloat(item.netAvailableQuantity || 0);
                              const componentQty = parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0);
                              if (!rmpmChecked) {
                                return "white";
                              }
                              if (allowUnderAllocation) {
                                const minAllowed = componentQty - (underAllocationLimit * componentQty / 100);
                                return netQty < minAllowed ? "#e0e0e0" : "white";
                              }
                             
                              
                              return netQty < componentQty ? "#e0e0e0" : "white";
                            })(),
                            cursor: (() => {
                              // Locked while a deplete instruction is held.
                              if (depleteInstructions[index]) {
                                return "not-allowed";
                              }
                              // Disabled cursor when single batch checked but over/under allocation not checked
                              if (rmpmChecked && !allowUnderAllocation && !allowOverAllocation) {
                                return "not-allowed";
                              }

                              if (rmpmChecked && (allowOverAllocation || allowUnderAllocation)) {
                                return selectedLot !== lotRowKey(item) ? "not-allowed" : "text";
                              }
                              const netQty = parseFloat(item.netAvailableQuantity || 0);
                              const componentQty = parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0);
                              if (!rmpmChecked) {
                                return "text";
                              }
                              if (allowUnderAllocation) {
                                const minAllowed = componentQty - (underAllocationLimit * componentQty / 100);
                                return netQty < minAllowed ? "not-allowed" : "text";
                              }
                             
                              
                              return netQty < componentQty ? "not-allowed" : "text";
                            })(),
                          },
                        }}
                        value={(() => {
                          // A held deplete instruction always shows Net Available Qty.,
                          // taking priority over the BOM-qty auto-fill below.
                          if (depleteInstructions[index]) {
                            return item.plannedAllocatedQuantity || "";
                          }
                          // When single batch is checked without over/under allocation and this lot is selected, show Material BoM Qty
                          if (rmpmChecked && !allowUnderAllocation && !allowOverAllocation && selectedLot === lotRowKey(item)) {
                            const componentQty = parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0);
                            return componentQty.toFixed(3);
                          }
                          return item.plannedAllocatedQuantity || "";
                        })()}
                        onChange={(e) => handleInputChange(index, e.target.value)}
                        fullWidth
                        size="small"
                        error={!!inputErrors[index]} // Highlight field with error border
                        disabled={(() => {
                          // Field is locked while a deplete instruction is
                          // held against this batch, regardless of mode.
                          if (depleteInstructions[index]) {
                            return true;
                          }
                          // Disable when single batch checked but over/under allocation not checked
                          if (rmpmChecked && !allowUnderAllocation && !allowOverAllocation) {
                            return true;
                          }

                          if (rmpmChecked && (allowOverAllocation || allowUnderAllocation)) {
                            return selectedLot !== lotRowKey(item);
                          }
                          // In multi-batch mode, allow editing for all rows (subject to item.disabled flag)
                          const netQty = parseFloat(item.netAvailableQuantity || 0);
                          const componentQty = parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0);
                          if (!rmpmChecked) {
                            return item.disabled;
                          }
                          if (allowUnderAllocation) {
                            const minAllowed = componentQty - (underAllocationLimit * componentQty / 100);
                            return netQty < minAllowed || item.disabled;
                          }
                          
                          return netQty < componentQty || item.disabled;
                        })()}
                      />
                    </Box>
                  </TableCell>
                  {/* Add Deplete Instruction checkbox — clickable on every row regardless
                      of whether a quantity has been entered or the row has been selected.
                      Checking it auto-populates Planned Allocated Qty. with Net Available
                      Qty. and locks the field; unchecking clears it. */}
                  <TableCell align="center">
                    <Checkbox
                      checked={depleteInstructions[index] || false}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const targetKey = lotRowKey(item);
                        const netQtyFormatted = formatNetAvailableQty(item.netAvailableQuantity);
                        let baseInventory = updatedInventory;

                        if (rmpmChecked) {
                          // Clear all stale row-level errors before switching or
                          // toggling — mirrors the radio button's own onChange, which does
                          // the same. Without this, an old row's over/under-allocation
                          // error (and its red border) persisted after switching the
                          // deplete-checked row, since handleInputChange below only ever
                          // updates the CURRENT row's error entry, not other indices'.
                          setErrorInfo("");
                          setInputErrors({});
                          setTotalAllocationError("");
                          setDisableReasonCode(false);

                          // Single batch allocation: only one row can be selected/depleted
                          // at a time. Checking deplete on a different row moves the
                          // selection to it, mirroring the manual radio-button behaviour.
                          if (isChecked && selectedLot !== targetKey) {
                            if (allowOverAllocation || allowUnderAllocation) {
                              // Only the previously-selected row could hold a value —
                              // every other row's field is disabled in this mode.
                              baseInventory = updatedInventory.map((lotItem) =>
                                lotRowKey(lotItem) === selectedLot
                                  ? { ...lotItem, plannedAllocatedQuantity: "" }
                                  : lotItem
                              );
                            } else {
                              // Exact-allocation mode: mirrors the radio button's own
                              // onChange, which clears EVERY other row (not just the
                              // previously-selected one) when switching selection —
                              // otherwise a deplete-populated qty on the old row lingers
                              // in state and would resurface if that row is re-selected.
                              baseInventory = updatedInventory.map((lotItem) =>
                                lotRowKey(lotItem) === targetKey
                                  ? lotItem
                                  : { ...lotItem, plannedAllocatedQuantity: "" }
                              );
                            }
                            setSelectedLot(targetKey);
                            setNetAvailableQty(parseFloat(item.netAvailableQuantity || 0));
                            setSelectedLotDataState(item);
                          }
                          // Only one row can carry a deplete instruction at a time here.
                          setDepleteInstructions(isChecked ? { [index]: true } : {});
                        } else {
                          setDepleteInstructions((prev) => ({
                            ...prev,
                            [index]: isChecked,
                          }));
                        }

                        handleInputChange(index, isChecked ? netQtyFormatted : "", baseInventory);

                        // Deplete-instruction limit check. Existing manual-entry
                        // validation branches are untouched — this only runs for a checked
                        // deplete instruction when neither "Do over allocation" nor "Do
                        // under allocation" is toggled (otherwise handleInputChange above
                        // already validated it against whichever limit is enabled). The
                        // deplete-driven quantity is always cross-checked against BoM Qty:
                        // greater than BoM Qty is treated as over-allocation and checked
                        // against the over-allocation limit; less than BoM Qty is treated
                        // as under-allocation and checked against the under-allocation limit.
                        if (isChecked && !allowOverAllocation && !allowUnderAllocation) {
                          const netQty = parseFloat(item.netAvailableQuantity || 0);
                          const bomQty = parseFloat(
                            lotData?.componentAllocationDetails?.componentQuantity || 0
                          );

                          if (rmpmChecked) {
                            let depleteErrorMsg = "";
                            if (netQty > bomQty) {
                              const maxAllowed = bomQty + (overAllocationLimit * bomQty) / 100;
                              if (netQty > maxAllowed) {
                                depleteErrorMsg = `Over-Allocation Limit Exceeded: The Planned Allocated Qty. (${netQty.toFixed(3)}) populated by the deplete instruction exceeds the maximum allowed over-allocation limit of ${maxAllowed.toFixed(3)} (Material BoM Qty: ${bomQty.toFixed(3)} + Over-allocation: ${overAllocationLimit}%).`;
                              }
                            } else if (netQty < bomQty) {
                              const minAllowed = bomQty - (underAllocationLimit * bomQty) / 100;
                              if (netQty < minAllowed) {
                                depleteErrorMsg = `Under-Allocation Limit Exceeded: The Planned Allocated Qty. (${netQty.toFixed(3)}) populated by the deplete instruction is below the minimum allowed under-allocation limit of ${minAllowed.toFixed(3)} (Material BoM Qty: ${bomQty.toFixed(3)} - Under-allocation: ${underAllocationLimit}%).`;
                              }
                            }
                            setInputErrors((prev) => ({ ...prev, [index]: depleteErrorMsg }));
                            setIsSaveEnabled(!depleteErrorMsg && comment.trim() !== "");
                          } else {
                            // baseInventory is the pre-update snapshot — handleInputChange's
                            // setUpdatedInventory above hasn't landed yet within this same
                            // synchronous handler, so this row's new value must be applied
                            // locally before summing, or the total would miss it entirely.
                            const totalQty = baseInventory.reduce(
                              (acc, invItem, i) =>
                                acc +
                                (i === index
                                  ? parseFloat(isChecked ? netQtyFormatted : 0) || 0
                                  : parseFloat(invItem.plannedAllocatedQuantity) || 0),
                              0
                            );
                            let depleteErrorMsg = "";
                            if (totalQty > bomQty) {
                              const maxAllowed = bomQty + (overAllocationLimit * bomQty) / 100;
                              if (totalQty > maxAllowed) {
                                depleteErrorMsg = `Over-Allocation Limit Exceeded: The total Planned Allocated Qty. (${totalQty.toFixed(3)}) exceeds the maximum allowed over-allocation limit of ${maxAllowed.toFixed(3)} (Material BoM Qty: ${bomQty.toFixed(3)} + Over-allocation: ${overAllocationLimit}%).`;
                              }
                            } else if (totalQty < bomQty) {
                              const minAllowed = bomQty - (underAllocationLimit * bomQty) / 100;
                              if (totalQty < minAllowed) {
                                depleteErrorMsg = `Under-Allocation Limit Exceeded: The total Planned Allocated Qty. (${totalQty.toFixed(3)}) is below the minimum allowed under-allocation limit of ${minAllowed.toFixed(3)} (Material BoM Qty: ${bomQty.toFixed(3)} - Under-allocation: ${underAllocationLimit}%).`;
                              }
                            }
                            setTotalAllocationError(depleteErrorMsg);
                            setIsSaveEnabled(!depleteErrorMsg);
                          }
                        }
                      }}
                      classes={{ checked: classes.customCheckbox }}
                    />
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* BCM-234 F9: deferred-SAP-push note — shown only when the checkbox
            is checked. Styled the same way as the other "Note —" lines in this
            dialog (plain caption inside a small-padding Grid). A bit of top
            space added so the note sits clearly below the batch table. */}
        {showOtherStorageLocations && (
          <Grid container style={{ paddingLeft: 3, paddingTop: 12, marginTop: 8 }}>
            <Typography variant="caption" style={{ paddingLeft: 4 }}>
              Note – If batches from other storage locations are selected, the whole
              allocation will be updated in SAP only when all the batches are in
              Special Stock with Vendor, with sufficient stock.
            </Typography>
          </Grid>
        )}

        <Grid container style={{ paddingLeft: 3, paddingTop: 3 }}>
          <Typography
            variant="caption"
            style={{ paddingLeft: 4 }}
          >
            {!rmpmChecked && !allowOverAllocation && !allowUnderAllocation
              ? "Note – Sum of planned allocated qty. should match with the Material BoM qty."
              : rmpmChecked
              ? allowOverAllocation
                ? `Note - Only those material batch numbers that have net available quantity within the over allocation limit (${parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0).toFixed(3)} to ${(parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0) + (overAllocationLimit * parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0) / 100)).toFixed(3)}) can be selected.`
                : allowUnderAllocation
                ? `Note - Only those material batch numbers that have net available quantity within the under allocation limit (${(parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0) - (underAllocationLimit * parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0) / 100)).toFixed(3)} to ${parseFloat(lotData?.componentAllocationDetails?.componentQuantity || 0).toFixed(3)}) can be selected.`
                : "Note - Only those material batch numbers that have net available quantity more than the BOM quantity can be selected."
              : ""}
          </Typography>
        </Grid>
        {/* Always render the error container with a small fixed height so
            the Save button doesn't jump when errors appear, but not so tall
            that it leaves a big empty gap above the Comment field. */}
        <Box sx={{ minHeight: 20, mb: 1 }}>
          <Grid container style={{ paddingLeft: 3, paddingTop: 3 }}>
            <ul style={{ margin: 0, paddingLeft: 20, minHeight: 0 }}>
              {/* Row-level errors */}
              {Object.entries(inputErrors).map(([idx, msg]) =>
                msg ? (
                  <li key={idx}>
                    <Typography variant="caption" style={{ color: "red" }}>
                      {msg}
                    </Typography>
                  </li>
                ) : null
              )}
              {/* Total-level error */}
              {totalAllocationError && (
                <li>
                  <Typography variant="caption" style={{ color: "red" }}>
                    {totalAllocationError}
                  </Typography>
                </li>
              )}
              {/* Allocation warning (BCM-613) — informational only; Save stays enabled */}
              {allocationWarning.message && (
                <li style={{ listStyle: "none", marginLeft: -20 }}>
                  <Typography variant="caption" style={{ color: "#ED6C02", fontWeight: 600 }}>
                    {allocationWarning.message}
                  </Typography>
                </li>
              )}
            </ul>
          </Grid>
        </Box>
        {errorInfo && (
          (allowOverAllocation || allowUnderAllocation || errorInfo === "Planned Allocated Qty.of a material batch number is more than the Net Available Qty.of that material batch.") && (
            <p style={{ color: "red", margin: "8px 0" }}>{errorInfo}</p>
          )
        )}
        {/* Commented out reason code dropdown */}
        {/* {lotData?.lotNumberAllocationDetails?.some(lot => lot.hasOwnProperty('plannedAllocatedQuantity')) && (
          <Grid container spacing={3} style={{ marginTop: "1rem" }}>
            <Grid item xs={4}>
              <Typography
                variant="subtitle2"
                style={{ marginBottom: "10px", fontWeight: 700 }}
              >
                Reason Code / Description
              </Typography>
              <FormControl
                className={classes.customSelect}
                style={{ width: "600px" }}
              >
                <Select
                  variant="outlined"
                  value={selectedReason}
                  onChange={(e) => {
                    const newReason = e.target.value;
                    setSelectedReason(newReason);
                    
                    // Check if lotNumberAllocationDetails exists but has no plannedAllocatedQuantity
                    const hasLotDetailsWithoutPlannedQty = 
                      Array.isArray(lotData?.lotNumberAllocationDetails) &&
                      lotData.lotNumberAllocationDetails.length > 0 &&
                      !lotData.lotNumberAllocationDetails.some(lot => lot.hasOwnProperty('plannedAllocatedQuantity'));

                    const isLotDetailsEmpty =
                      Array.isArray(lotData?.lotNumberAllocationDetails) &&
                      lotData?.lotNumberAllocationDetails.length === 0;

                    // Require radio selection when checkbox is checked
                    if (rmpmChecked) {
                      // Get the selected lot's entered planned allocated quantity value
                      const selectedLotItem = updatedInventory?.find(item => lotRowKey(item) === selectedLot) || null;
                      const enteredPlannedQty = selectedLotItem ? parseFloat(selectedLotItem.plannedAllocatedQuantity || 0) : 0;
                      const hasEnteredValue = enteredPlannedQty > 0;
                      
                      if (hasLotDetailsWithoutPlannedQty || isLotDetailsEmpty) {
                        // When over/under allocation is enabled, require value to be entered
                        if (allowOverAllocation || allowUnderAllocation) {
                          setIsSaveEnabled(selectedLot !== null && hasEnteredValue);
                        } else {
                          // No over/under allocation: value is auto-set, just need radio
                          setIsSaveEnabled(selectedLot !== null);
                        }
                      } else {
                       
                        if (allowOverAllocation || allowUnderAllocation) {
                          setIsSaveEnabled(
                            selectedLot !== null &&
                            hasEnteredValue &&
                              ((newReason.trim() !== "" &&
                                newReason.trim() !== "EXMRP019") ||
                                (newReason.trim() === "EXMRP019" &&
                                  comment.trim() !== "") ||
                                (newReason.trim() === "" && comment.trim() !== ""))
                          );
                        } else {
                          setIsSaveEnabled(
                            selectedLot !== null &&
                              ((newReason.trim() !== "" &&
                                newReason.trim() !== "EXMRP019") ||
                                (newReason.trim() === "EXMRP019" &&
                                  comment.trim() !== "") ||
                                (newReason.trim() === "" && comment.trim() !== ""))
                          );
                        }
                      }
                    } else {
                      const requiredQuantity = parseFloat(
                        lotData?.componentAllocationDetails
                          ?.componentQuantity || 0
                      );
                      const totalEnteredQuantity = updatedInventory.reduce(
                        (acc, item) =>
                          acc +
                          (parseFloat(item.plannedAllocatedQuantity) || 0),
                        0
                      );
                      const isExactMatch =
                        totalEnteredQuantity.toFixed(3) ===
                        requiredQuantity.toFixed(3);

                      const areAllLotNumberDetailsEmpty = lotAllocationData?.every(
                        (item) => item?.lotNumberAllocationDetails?.length === 0
                      );

                      if (areAllLotNumberDetailsEmpty) {
                        setIsSaveEnabled(
                          isExactMatch ||
                            newReason.trim() !== "" ||
                            comment.trim() !== ""
                        );
                      } else {
                        setIsSaveEnabled(
                          isExactMatch &&
                            ((newReason.trim() !== "" &&
                              newReason.trim() !== "EXMRP019") ||
                              (newReason.trim() === "EXMRP019" &&
                                comment.trim() !== ""))
                        );
                      }
                    }
                  }}
                  displayEmpty
                  className={classes.customSelectSty}
                  disabled={disableReasonCode || errorInfo}
                >
                  <MenuItem value="" disabled>
                    Select Reason Code
                  </MenuItem>
                  {reasonCodes?.map((reason) => (
                    <MenuItem key={reason.reasonCode} value={reason.reasonCode}>
                      {reason.reasonCode} / {reason.reasonDescription || ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              <Typography
                variant="subtitle2"
                style={{ marginBottom: "10px", fontWeight: 700 }}
              >
                Reason code description
              </Typography>
              <Typography>
                {reasonCodes?.find((r) => r.reasonCode === selectedReason)
                  ?.reasonDescription || "-"}
              </Typography>
            </Grid>
          </Grid>
        )} */}
        {lotData?.lotNumberAllocationDetails?.some(lot => lot.hasOwnProperty('plannedAllocatedQuantity')) && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  style={{ marginBottom: "10px", fontWeight: 700 }}
                >
                  Comment<span style={{ color: "red" }}>  *</span>
                </Typography>
                <TextField
                className={`edit-material-comment ${classes.textField} ${classes.commentField}`}
                  fullWidth
                  multiline
                  rows={4}
                  value={comment}
                  onChange={(e) => {
                     if(e.target.value.length < 256){
                    setComment(e.target.value);
                  }
                    
                    // Check if lotNumberAllocationDetails exists but has no plannedAllocatedQuantity
                    const hasLotDetailsWithoutPlannedQty = 
                      Array.isArray(lotData?.lotNumberAllocationDetails) &&
                      lotData.lotNumberAllocationDetails.length > 0 &&
                      !lotData.lotNumberAllocationDetails.some(lot => lot.hasOwnProperty('plannedAllocatedQuantity'));

                    const isLotDetailsEmpty =
                      Array.isArray(lotData?.lotNumberAllocationDetails) &&
                      lotData?.lotNumberAllocationDetails.length === 0;

                    // Require radio selection when checkbox is checked
                    if (rmpmChecked) {
                      // Get the selected lot's entered planned allocated quantity value
                      const selectedLotItem = updatedInventory?.find(item => lotRowKey(item) === selectedLot) || null;
                      const enteredPlannedQty = selectedLotItem ? parseFloat(selectedLotItem.plannedAllocatedQuantity || 0) : 0;
                      const hasEnteredValue = enteredPlannedQty > 0;
                      
                      if (hasLotDetailsWithoutPlannedQty || isLotDetailsEmpty) {
                        // When over/under allocation is enabled, require value to be entered
                        if (allowOverAllocation || allowUnderAllocation) {
                          setIsSaveEnabled(selectedLot !== null && hasEnteredValue);
                        } else {
                          // No over/under allocation: value is auto-set, just need radio
                          setIsSaveEnabled(selectedLot !== null);
                        }
                      } else {
                       
                        if (allowOverAllocation || allowUnderAllocation) {
                          setIsSaveEnabled(
                            selectedLot !== null &&
                            hasEnteredValue &&
                              // ((selectedReason !== "" &&
                              //   selectedReason !== "EXMRP019") ||
                              //   (selectedReason === "EXMRP019" &&
                              //     e.target.value.trim() !== "") ||
                              //   (selectedReason === "" && e.target.value.trim() !== ""))
                               e.target.value.trim() !== ""
                          );
                        } else {
                          setIsSaveEnabled(
                            selectedLot !== null &&
                              // ((selectedReason !== "" &&
                              //   selectedReason !== "EXMRP019") ||
                              //   (selectedReason === "EXMRP019" &&
                              //     e.target.value.trim() !== "") ||
                              //   (selectedReason === "" && e.target.value.trim() !== ""))
                              e.target.value.trim() !== ""
                          );
                        }
                      }
                    } else {
                      const requiredQuantity = parseFloat(
                        lotData?.componentAllocationDetails?.componentQuantity ||
                          0
                      );
                      const totalEnteredQuantity = updatedInventory.reduce(
                        (acc, item) =>
                          acc + (parseFloat(item.plannedAllocatedQuantity) || 0),
                        0
                      );
                      const isExactMatch =
                        totalEnteredQuantity.toFixed(3) ===
                        requiredQuantity.toFixed(3);

                      const areAllLotNumberDetailsEmpty = lotAllocationData?.every(
                        (item) => item?.lotNumberAllocationDetails?.length === 0
                      );

                      if (areAllLotNumberDetailsEmpty) {
                        setIsSaveEnabled(
                          // isExactMatch ||
                          //   selectedReason.trim() !== "" ||
                           isExactMatch || e.target.value.trim() !== ""
                        );
                      } else {
                        setIsSaveEnabled(
                          isExactMatch &&
                            // ((selectedReason !== "" &&
                            //   selectedReason !== "EXMRP019") ||
                            //   (selectedReason === "EXMRP019" &&
                            //     e.target.value.trim() !== ""))
                            e.target.value.trim() !== ""
                        );
                      }
                    }
                  }}
                  disabled={disableReasonCode || errorInfo}
                  variant="outlined"
                  size="small"
                  placeholder="Enter text here"
                  // InputProps={{
                  //   sx: {
                  //     padding: 0, // Remove outer padding
                  //     '& textarea': {
                  //       padding: '4px',      // Adjust as needed
                  //       fontSize: '12px',    // Smaller text
                  //       lineHeight: '1.2',   // Tighter spacing
                  //       height: '0.5rem',    // Effective height (won’t be exact)
                  //     },
                  //   },
                  // }}
                />

              {/* <Typography variant="caption" style={{ color: error && "red" }}>
                Note - If reason code is not entered, comment field is
                mandatory.
              </Typography> */}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        style={{
          justifyContent: "center", // Centers the button
          // paddingBottom: "16px", // Adds spacing at bottom
          marginBottom:"16px"
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={!isSaveEnabled || disableReasonCode}
          endIcon={<ChevronRight />}
          style={{
            borderRadius: "20px",
            textTransform: "none",
          }}
          onClick={handleSave}
        >
          Save changes
        </Button>
      </DialogActions>

      {/* Legend for Material Near Expiry / Expired Flag */}
      {(() => {
        const rows = updatedInventory?.length > 0 ? updatedInventory : lotData?.inventoryDetails || [];
        const hasNearExpiry = rows.some(item => item?.batchNearExpiry === true);
        const hasExpired = rows.some(item => item?.inventoryExpiryDate && new Date(item.inventoryExpiryDate) < new Date());
        if (!hasNearExpiry && !hasExpired) return null;
        return (
          <div style={{
            margin: "1rem",
            padding: "0.5rem",
            backgroundColor: "#f9f9f9",
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: "0.875rem"
          }}>
            <Typography variant="caption" style={{ fontSize: "0.875rem" }}>Legend:</Typography>
            {hasNearExpiry && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <FlagIcon style={{ color: "yellow", fontSize: 20, stroke: "black", strokeWidth: 1.5 }} />
                <Typography variant="caption" style={{ fontSize: "0.875rem" }}>Material near expiry</Typography>
              </div>
            )}
            {hasExpired && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <FlagIcon style={{ color: "red", fontSize: 20, stroke: "black", strokeWidth: 1.5 }} />
                <Typography variant="caption" style={{ fontSize: "0.875rem" }}>Batch expired</Typography>
              </div>
            )}
          </div>
        );
      })()}
    </React.Fragment>
  );
};

export default EditPlannedAllocatedQuantityDialog;
